import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { useToast } from "../hooks/use-toast";
import {
  adminLogin,
  API_BASE_URL,
  createAdminVendorWhatsappTemplate,
  createAdminCoupon,
  createAdminCategory,
  createVendor,
  createProduct,
  deleteAdminVendorWhatsappTemplate,
  deleteAdminCategory,
  deleteAdminProductSize,
  deleteAdminReview,
  disableAdminCoupon,
  deleteVendor,
  deleteProduct,
  getAdminCoupons,
  getAdminCategories,
  getAdminProductBrands,
  getAdminProductColors,
  getAdminProductSizes,
  getAdminOrders,
  getAdminReviews,
  getAdminVendorWhatsappTemplates,
  getAdminNewsletterSubscribers,
  getAdminVendors,
  getHomeSettings,
  getPrivacyPolicyContent,
  getPurchaseMessageContent,
  getProducts,
  sendVendorWelcomeEmail,
  updatePrivacyPolicyContent,
  updateAdminCategory,
  updateAdminVendorWhatsappTemplate,
  getTermsContent,
  updateHomeSettings,
  updatePurchaseMessageContent,
  updateProduct,
  updateTermsContent,
} from "../lib/api";
import Seo from "../components/Seo";
import { calculateDiscountPercent } from "../lib/pricing";

const DEFAULT_PRODUCT_CATEGORIES = [
  { value: "hombre", label: "Hombre", template_slug: "hombre" },
  { value: "mujer", label: "Mujer", template_slug: "mujer" },
  {
    value: "unisex",
    label: "Publicar en ambas categorias Hombre y Mujer",
    template_slug: "hombre",
  },
  { value: "accesorios", label: "Accesorios", template_slug: "accesorios" },
];

const DEFAULT_VENDOR_WELCOME_MESSAGE =
  "Bienvenido a Eclipse Tenis, {nombre}. Nos alegra contar contigo.";

const createEmptyVendorTemplateForm = () => ({
  name: "",
  message: DEFAULT_VENDOR_WELCOME_MESSAGE,
});

const CATEGORY_TEMPLATE_OPTIONS = [
  { value: "hombre", label: "Plantilla Hombre (con tallaje de hombre)" },
  { value: "mujer", label: "Plantilla Mujer (con tallaje de mujer)" },
  { value: "accesorios", label: "Plantilla Accesorios (sin tallaje de calzado)" },
];

const EMPTY_CATEGORY_FORM = {
  name: "",
  slug: "",
  description: "",
  templateSlug: "hombre",
  cloneSourceCategoryId: "",
  propertyBrands: "",
  propertyColors: "",
  propertySizes: "",
  isActive: true,
  sortOrder: "",
};

const getCategoryDisplayLabel = (slug, fallbackName) => {
  if (slug === "unisex") {
    return "Publicar en ambas categorias Hombre y Mujer";
  }
  return fallbackName;
};

const parseSizeList = (value) => {
  if (!value) return [];
  return String(value)
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
};

const normalizeSizeValue = (value, prefix) => {
  if (!value) return "";
  const trimmed = String(value).trim();
  const prefixRegex = prefix ? new RegExp(`^${prefix}\\s*`, "i") : null;
  return prefixRegex ? trimmed.replace(prefixRegex, "").trim() : trimmed;
};

const parseSizePair = (value) => {
  if (!value) return null;
  const trimmed = String(value).trim();
  if (!trimmed) return null;
  const usMatch = trimmed.match(/US\s*([0-9./]+)/i);
  const euMatch = trimmed.match(/EUR\s*([0-9./]+)/i);
  const cmMatch = trimmed.match(/CM\s*([0-9./]+)/i);
  if (usMatch || euMatch || cmMatch) {
    return {
      us: usMatch ? usMatch[1] : "",
      eu: euMatch ? euMatch[1] : "",
      cm: cmMatch ? cmMatch[1] : "",
      selected: true,
    };
  }
  const inlineParts = trimmed
    .split("/")
    .map((item) => item.trim())
    .filter(Boolean);
  if (inlineParts.length >= 2) {
    const us = normalizeSizeValue(inlineParts[0], "US");
    const eu = normalizeSizeValue(inlineParts[1], "EUR");
    const cm = normalizeSizeValue(inlineParts[2], "CM");
    return { us, eu, cm, selected: true };
  }
  const us = normalizeSizeValue(trimmed, "US");
  return { us, eu: "", cm: "", selected: true };
};

const coerceSizePairs = (sizes) => {
  const list = Array.isArray(sizes) ? sizes : parseSizeList(sizes);
  return list
    .map((item) => parseSizePair(item))
    .filter((pair) => pair && (pair.us || pair.eu || pair.cm));
};

const mergeSizePairs = (base, incoming) => {
  const seen = new Set();
  const merged = [];
  [...base, ...incoming].forEach((pair) => {
    const normalized = {
      us: normalizeSizeValue(pair.us, "US"),
      eu: normalizeSizeValue(pair.eu, "EUR"),
      cm: normalizeSizeValue(pair.cm, "CM"),
      selected: Boolean(pair.selected),
    };
    if (!normalized.us && !normalized.eu && !normalized.cm) {
      return;
    }
    const key = `${normalized.us}|${normalized.eu}|${normalized.cm}`.toLowerCase();
    if (seen.has(key)) {
      return;
    }
    seen.add(key);
    merged.push(normalized);
  });
  return merged;
};

const createSizePair = (us = "", eu = "", cm = "", selected = true) => ({
  us,
  eu,
  cm,
  selected,
});

const splitSizeEditorEntries = (value) =>
  String(value || "")
    .replace(/\r/g, "")
    .split(/\n|;/)
    .flatMap((chunk) => chunk.split(/,(?=\s*US\b)/i))
    .map((item) => item.trim())
    .filter(Boolean);

const formatSizePairLabel = (pair) => {
  const us = normalizeSizeValue(pair?.us, "US");
  const eu = normalizeSizeValue(pair?.eu, "EUR");
  const cm = normalizeSizeValue(pair?.cm, "CM");
  const parts = [];
  if (us) parts.push(`US ${us}`);
  if (eu) parts.push(`EUR ${eu}`);
  if (cm) parts.push(`CM ${cm}`);
  return parts.join(" / ");
};

const getSizePairKey = (pair) => {
  const us = normalizeSizeValue(pair?.us, "US");
  const eu = normalizeSizeValue(pair?.eu, "EUR");
  const cm = normalizeSizeValue(pair?.cm, "CM");
  if (!us && !eu && !cm) return "";
  return `${us}|${eu}|${cm}`.toLowerCase();
};

const formatSizePairsForEditor = (pairs) =>
  (Array.isArray(pairs) ? pairs : [])
    .filter((pair) => Boolean(pair?.selected) || pair?.us || pair?.eu || pair?.cm)
    .map((pair) => formatSizePairLabel(pair))
    .filter(Boolean)
    .join("\n");

const parseSizeEditorPairs = (value) =>
  splitSizeEditorEntries(value)
    .map((item) => parseSizePair(item))
    .filter((pair) => pair && (pair.us || pair.eu || pair.cm));

const parseColorTokens = (value) =>
  String(value || "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);

const parseConfigTokens = (value) =>
  String(value || "")
    .replace(/\r/g, "\n")
    .split(/[\n,;]+/)
    .map((item) => item.trim())
    .filter(Boolean)
    .filter((item, index, array) => array.findIndex((entry) => entry.toLowerCase() === item.toLowerCase()) === index);

const formatConfigTokens = (values) =>
  (Array.isArray(values) ? values : [])
    .map((item) => String(item || "").trim())
    .filter(Boolean)
    .join("\n");

const normalizeCategoryProperties = (value = {}) => ({
  brands: Array.isArray(value?.brands)
    ? value.brands.map((item) => String(item || "").trim()).filter(Boolean)
    : [],
  colors: Array.isArray(value?.colors)
    ? value.colors.map((item) => String(item || "").trim()).filter(Boolean)
    : [],
  sizes: Array.isArray(value?.sizes)
    ? value.sizes.map((item) => String(item || "").trim()).filter(Boolean)
    : [],
});

const buildCategoryPropertySummary = (value = {}) => {
  const properties = normalizeCategoryProperties(value);
  const summary = [];
  if (properties.brands.length > 0) summary.push(`${properties.brands.length} marcas`);
  if (properties.colors.length > 0) summary.push(`${properties.colors.length} colores`);
  if (properties.sizes.length > 0) summary.push(`${properties.sizes.length} tallas`);
  return summary;
};

const DEFAULT_TOP_BANNER_TEXT =
  "Compra a cuotas con Sistecredito | Envio gratis desde $200.000 | Recibe un cupon del 5% en compras superiores a $200.000";
const DEFAULT_CREDIT_PROMO_TEXT = "Compralo hoy y pagalo luego a credito";

const EMPTY_HOME_IMAGES = {
  banner_1: "",
  banner_2: "",
  banner_3: "",
  banner_4: "",
  hombre: "",
  mujer: "",
  accesorios: "",
  payment_1: "",
  payment_2: "",
  payment_3: "",
  payment_4: "",
  payment_5: "",
  payment_6: "",
  payment_7: "",
  payment_8: "",
  payment_9: "",
  payment_10: "",
};

const HERO_IMAGE_FIELDS = [
  { key: "banner_1", label: "Banner principal 1" },
  { key: "banner_2", label: "Banner principal 2" },
  { key: "banner_3", label: "Banner principal 3" },
  { key: "banner_4", label: "Banner principal 4" },
];

const DEFAULT_HOME_BANNER_CONTENT = {
  banner_1: {
    title: "Nuevos Modelos 2026",
    subtitle: "Descubre las ultimas tendencias en zapatillas",
    cta: "Ver Modelos",
    link: "/catalogo/hombre",
    steps: [],
  },
  banner_2: {
    title: "WhatsApp para dudas y novedades",
    subtitle: "Atencion inmediata sobre compras y pagos con Sistecredito",
    cta: "Hablar por WhatsApp",
    link: "https://wa.me/573184215616?text=Hola%20estoy%20interesado%20en%20un%20producto",
    steps: [],
  },
  banner_3: {
    title: "Correr y entrenar",
    subtitle: "Maximo rendimiento para tus entrenamientos",
    cta: "Ver Coleccion",
    link: "/catalogo/hombre",
    steps: [],
  },
  banner_4: {
    title: "Compra a credito Sistecredito",
    subtitle: "Proceso rapido y seguro para pagar a cuotas",
    cta: "Ir al checkout",
    link: "/checkout",
    steps: [
      "Agrega tus productos al carrito.",
      "En el pago, elige Sistecredito.",
      "Ingresa documento, celular y correo.",
      "Selecciona cuotas y acepta terminos.",
      "Confirma con codigo por WhatsApp.",
      "Despacho en 24 a 48 horas.",
    ],
  },
};

const CATEGORY_IMAGE_FIELDS = [
  { key: "hombre", label: "Categoria Hombre" },
  { key: "mujer", label: "Categoria Mujer" },
  { key: "accesorios", label: "Categoria Accesorios" },
];

const DEFAULT_CATEGORY_BANNER_CONTENT = {
  hombre: {
    title: "Hombre",
    subtitle: "",
  },
  mujer: {
    title: "Mujer",
    subtitle: "",
  },
  accesorios: {
    title: "Accesorios",
    subtitle: "",
  },
};

const PAYMENT_IMAGE_FIELDS = Array.from({ length: 10 }, (_, index) => ({
  key: `payment_${index + 1}`,
  label: `Metodo de pago ${index + 1}`,
}));

const cloneHomeBannerContent = (source = DEFAULT_HOME_BANNER_CONTENT) =>
  JSON.parse(JSON.stringify(source));

const cloneCategoryBannerContent = (source = DEFAULT_CATEGORY_BANNER_CONTENT) =>
  JSON.parse(JSON.stringify(source));

const normalizeHomeBannerEntry = (value = {}, fallback = {}) => {
  const normalizedSteps = Array.isArray(value?.steps)
    ? value.steps.map((step) => String(step || "").trim()).filter(Boolean)
    : Array.isArray(fallback?.steps)
      ? fallback.steps.map((step) => String(step || "").trim()).filter(Boolean)
      : [];
  return {
    title: String(value?.title || fallback?.title || "").trim(),
    subtitle: String(value?.subtitle || fallback?.subtitle || "").trim(),
    cta: String(value?.cta || fallback?.cta || "").trim(),
    link: String(value?.link || fallback?.link || "").trim(),
    steps: normalizedSteps,
  };
};

const normalizeCategoryBannerEntry = (value = {}, fallback = {}) => ({
  title:
    value && Object.prototype.hasOwnProperty.call(value, "title")
      ? String(value?.title || "").trim()
      : String(fallback?.title || "").trim(),
  subtitle:
    value && Object.prototype.hasOwnProperty.call(value, "subtitle")
      ? String(value?.subtitle || "").trim()
      : String(fallback?.subtitle || "").trim(),
});

const buildHomeSettingsState = (
  data = {},
  fallbackImages = EMPTY_HOME_IMAGES,
  fallbackTopText = DEFAULT_TOP_BANNER_TEXT,
  fallbackPromoText = DEFAULT_CREDIT_PROMO_TEXT,
  fallbackBanners = DEFAULT_HOME_BANNER_CONTENT,
  fallbackCategoryBanners = DEFAULT_CATEGORY_BANNER_CONTENT,
) => ({
  topBannerText: data?.top_banner_text || fallbackTopText,
  creditPromoText: data?.credit_promo_text || fallbackPromoText,
  homeBannerContent: HERO_IMAGE_FIELDS.reduce((accumulator, field) => {
    accumulator[field.key] = normalizeHomeBannerEntry(
      data?.hero_banners?.[field.key],
      fallbackBanners?.[field.key],
    );
    return accumulator;
  }, {}),
  categoryBannerContent: CATEGORY_IMAGE_FIELDS.reduce((accumulator, field) => {
    accumulator[field.key] = normalizeCategoryBannerEntry(
      data?.category_banners?.[field.key],
      fallbackCategoryBanners?.[field.key],
    );
    return accumulator;
  }, {}),
  homeImages: {
    banner_1: data?.hero_images?.banner_1 || fallbackImages.banner_1 || "",
    banner_2: data?.hero_images?.banner_2 || fallbackImages.banner_2 || "",
    banner_3: data?.hero_images?.banner_3 || fallbackImages.banner_3 || "",
    banner_4: data?.hero_images?.banner_4 || fallbackImages.banner_4 || "",
    hombre: data?.category_images?.hombre || fallbackImages.hombre || "",
    mujer: data?.category_images?.mujer || fallbackImages.mujer || "",
    accesorios: data?.category_images?.accesorios || fallbackImages.accesorios || "",
    payment_1: data?.payment_images?.payment_1 || fallbackImages.payment_1 || "",
    payment_2: data?.payment_images?.payment_2 || fallbackImages.payment_2 || "",
    payment_3: data?.payment_images?.payment_3 || fallbackImages.payment_3 || "",
    payment_4: data?.payment_images?.payment_4 || fallbackImages.payment_4 || "",
    payment_5: data?.payment_images?.payment_5 || fallbackImages.payment_5 || "",
    payment_6: data?.payment_images?.payment_6 || fallbackImages.payment_6 || "",
    payment_7: data?.payment_images?.payment_7 || fallbackImages.payment_7 || "",
    payment_8: data?.payment_images?.payment_8 || fallbackImages.payment_8 || "",
    payment_9: data?.payment_images?.payment_9 || fallbackImages.payment_9 || "",
    payment_10: data?.payment_images?.payment_10 || fallbackImages.payment_10 || "",
  },
});

const ADMIN_MODULES = [
  { key: "productos", label: "Productos" },
  { key: "categorias", label: "Categorias" },
  { key: "pedidos", label: "Pedidos" },
  { key: "terminos", label: "Terminos y condiciones" },
  { key: "privacidad", label: "Politica de privacidad" },
  { key: "home", label: "Configuracion Home" },
  { key: "vendedores", label: "Vendedores" },
  { key: "newsletter", label: "Suscripciones" },
  { key: "cupones", label: "Cupones" },
  { key: "mensaje_final", label: "Mensaje final" },
  { key: "resenas", label: "Reseñas" },
];

const Admin = () => {
  const [token, setToken] = useState(localStorage.getItem("admin_token") || "");
  const [loginForm, setLoginForm] = useState({ username: "", password: "" });
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeModule, setActiveModule] = useState("productos");
  const [adminCategories, setAdminCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [categorySaving, setCategorySaving] = useState(false);
  const [editingCategoryId, setEditingCategoryId] = useState(null);
  const [categoryForm, setCategoryForm] = useState(EMPTY_CATEGORY_FORM);
  const [categoryImageFile, setCategoryImageFile] = useState(null);
  const [categoryImageFallback, setCategoryImageFallback] = useState("");
  const [categoryImagePreview, setCategoryImagePreview] = useState("");
  const [categoryImageInputVersion, setCategoryImageInputVersion] = useState(0);
  const [categoryUseSourceImage, setCategoryUseSourceImage] = useState(false);
  const [productos, setProductos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    name: "",
    brand: "",
    category: "hombre",
    color: "",
    stock: "",
    whatsappOnly: false,
    price: "",
    salePrice: "",
    description: "",
  });
  const [sizePairs, setSizePairs] = useState([]);
  const [sizeEditorText, setSizeEditorText] = useState("");
  const [imageFiles, setImageFiles] = useState([]);
  const [previewUrls, setPreviewUrls] = useState([]);
  const imageInputRef = useRef(null);
  const [termsContent, setTermsContent] = useState("");
  const [termsLoading, setTermsLoading] = useState(false);
  const [termsSaving, setTermsSaving] = useState(false);
  const [privacyContent, setPrivacyContent] = useState("");
  const [privacyLoading, setPrivacyLoading] = useState(false);
  const [privacySaving, setPrivacySaving] = useState(false);
  const [homeSettingsLoading, setHomeSettingsLoading] = useState(false);
  const [homeSettingsSaving, setHomeSettingsSaving] = useState(false);
  const [topBannerText, setTopBannerText] = useState(DEFAULT_TOP_BANNER_TEXT);
  const [creditPromoText, setCreditPromoText] = useState(DEFAULT_CREDIT_PROMO_TEXT);
  const [homeBannerContent, setHomeBannerContent] = useState(cloneHomeBannerContent());
  const [categoryBannerContent, setCategoryBannerContent] = useState(
    cloneCategoryBannerContent(),
  );
  const [homeImages, setHomeImages] = useState(EMPTY_HOME_IMAGES);
  const [homeImageFiles, setHomeImageFiles] = useState({});
  const [homeImageInputVersion, setHomeImageInputVersion] = useState(0);
  const [vendors, setVendors] = useState([]);
  const [vendorsLoading, setVendorsLoading] = useState(false);
  const [vendorSaving, setVendorSaving] = useState(false);
  const [vendorTemplates, setVendorTemplates] = useState([]);
  const [vendorTemplatesLoading, setVendorTemplatesLoading] = useState(false);
  const [vendorTemplateSaving, setVendorTemplateSaving] = useState(false);
  const [vendorTemplateDeletingId, setVendorTemplateDeletingId] = useState(null);
  const [editingVendorTemplateId, setEditingVendorTemplateId] = useState(null);
  const [vendorTemplateForm, setVendorTemplateForm] = useState(
    createEmptyVendorTemplateForm(),
  );
  const [vendorWelcomeSending, setVendorWelcomeSending] = useState(false);
  const [vendorWelcomeForm, setVendorWelcomeForm] = useState({
    vendorId: "",
    templateId: "",
    message: DEFAULT_VENDOR_WELCOME_MESSAGE,
  });
  const [newsletterSubscribers, setNewsletterSubscribers] = useState([]);
  const [newsletterLoading, setNewsletterLoading] = useState(false);
  const [coupons, setCoupons] = useState([]);
  const [couponsLoading, setCouponsLoading] = useState(false);
  const [couponSaving, setCouponSaving] = useState(false);
  const [couponForm, setCouponForm] = useState({
    discountType: "percent",
    percent: "5",
    amountOff: "",
    minAmount: "0",
    codePrefix: "ET",
  });
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [purchaseMessageContent, setPurchaseMessageContent] = useState("");
  const [purchaseMessageLoading, setPurchaseMessageLoading] = useState(false);
  const [purchaseMessageSaving, setPurchaseMessageSaving] = useState(false);
  const [productBrandOptions, setProductBrandOptions] = useState([]);
  const [productColorOptions, setProductColorOptions] = useState([]);
  const [productSizeOptions, setProductSizeOptions] = useState([]);
  const [vendorForm, setVendorForm] = useState({
    name: "",
    idNumber: "",
    email: "",
    address: "",
    whatsappPhone: "",
  });
  const { toast } = useToast();

  const editingProduct = useMemo(
    () => productos.find((item) => item.id === editingId) || null,
    [productos, editingId],
  );
  const selectedVendorTemplate = useMemo(
    () =>
      vendorTemplates.find(
        (template) => String(template.id) === String(vendorWelcomeForm.templateId),
      ) || null,
    [vendorTemplates, vendorWelcomeForm.templateId],
  );
  const productCategoryOptions = useMemo(() => {
    if (adminCategories.length > 0) {
      return adminCategories.map((item) => ({
        value: item.slug,
        label: getCategoryDisplayLabel(item.slug, item.name),
        is_active: Boolean(item.is_active),
        template_slug: item.template_slug || (item.slug === "accesorios" ? "accesorios" : "hombre"),
        properties: normalizeCategoryProperties(item.properties),
      }));
    }
    return DEFAULT_PRODUCT_CATEGORIES.map((item) => ({
      value: item.value,
      label: item.label,
      is_active: true,
      template_slug: item.template_slug,
      properties: normalizeCategoryProperties(),
    }));
  }, [adminCategories]);
  const selectedProductCategory = useMemo(
    () =>
      productCategoryOptions.find((category) => category.value === form.category) ||
      DEFAULT_PRODUCT_CATEGORIES.find((category) => category.value === form.category) ||
      null,
    [productCategoryOptions, form.category],
  );
  const getCategoryTemplateSlug = (categoryValue) => {
    const selectedCategory =
      productCategoryOptions.find((category) => category.value === categoryValue) ||
      DEFAULT_PRODUCT_CATEGORIES.find((category) => category.value === categoryValue);
    if (selectedCategory?.template_slug) {
      return selectedCategory.template_slug;
    }
    return categoryValue === "accesorios" ? "accesorios" : "hombre";
  };
  const isProductFootwear =
    selectedProductCategory?.template_slug === "hombre" ||
    selectedProductCategory?.template_slug === "mujer";
  const selectedCategoryCloneSource = useMemo(
    () =>
      adminCategories.find(
        (item) => String(item.id) === String(categoryForm.cloneSourceCategoryId || ""),
      ) || null,
    [adminCategories, categoryForm.cloneSourceCategoryId],
  );
  const filteredColorOptions = useMemo(() => {
    const fromApi = Array.isArray(productColorOptions) ? productColorOptions : [];
    const fromCategory = selectedProductCategory?.properties?.colors || [];
    const fromProducts = productos
      .filter((item) => {
        if (!item?.category) return true;
        if (form.category === "unisex") {
          return ["hombre", "mujer", "unisex"].includes(item.category);
        }
        if (form.category === "hombre" || form.category === "mujer") {
          return item.category === form.category || item.category === "unisex";
        }
        return item.category === form.category;
      })
      .flatMap((item) => parseColorTokens(item.color));

    const merged = [...fromCategory, ...fromApi, ...fromProducts];
    const seen = new Set();
    return merged
      .map((item) => String(item || "").trim())
      .filter(Boolean)
      .filter((item) => {
        const key = item.toLowerCase();
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .sort((a, b) => a.localeCompare(b, "es"));
  }, [productColorOptions, productos, form.category, selectedProductCategory]);
  const filteredBrandOptions = useMemo(() => {
    const fromApi = Array.isArray(productBrandOptions) ? productBrandOptions : [];
    const fromCategory = selectedProductCategory?.properties?.brands || [];
    const fromProducts = productos
      .filter((item) => {
        if (!item?.category) return true;
        if (form.category === "unisex") {
          return ["hombre", "mujer", "unisex"].includes(item.category);
        }
        if (form.category === "hombre" || form.category === "mujer") {
          return item.category === form.category || item.category === "unisex";
        }
        return item.category === form.category;
      })
      .map((item) => String(item.brand || "").trim())
      .filter(Boolean);

    const merged = [...fromCategory, ...fromApi, ...fromProducts];
    const seen = new Set();
    return merged
      .map((item) => String(item || "").trim())
      .filter(Boolean)
      .filter((item) => {
        const key = item.toLowerCase();
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .sort((a, b) => a.localeCompare(b, "es"));
  }, [productBrandOptions, productos, form.category, selectedProductCategory]);
  const suggestedSizePairs = useMemo(
    () =>
      mergeSizePairs(
        coerceSizePairs(selectedProductCategory?.properties?.sizes || []),
        coerceSizePairs(productSizeOptions),
      ),
    [productSizeOptions, selectedProductCategory],
  );
  const currentSizePairKeys = useMemo(
    () => new Set(sizePairs.map((pair) => getSizePairKey(pair)).filter(Boolean)),
    [sizePairs],
  );

  const formatCop = (value) =>
    `$${Number(value || 0).toLocaleString("es-CO")}`;

  const formatDateTime = (value) => {
    if (!value) return "-";
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return "-";
    return parsed.toLocaleString("es-CO");
  };

  const formatCouponDiscount = (coupon) => {
    const amountOff = Number(coupon?.amount_off || 0);
    if (amountOff > 0) {
      return formatCop(amountOff);
    }
    const percent = Number(coupon?.percent || 0);
    if (percent <= 0) {
      return "Sin descuento";
    }
    return `${Math.round(percent * 10000) / 100}%`;
  };

  const formatCouponTimeLeft = (seconds) => {
    if (seconds === null || seconds === undefined) {
      return "Sin vencimiento";
    }
    const totalSeconds = Number(seconds);
    if (!Number.isFinite(totalSeconds)) {
      return "Sin vencimiento";
    }
    if (totalSeconds <= 0) {
      return "Vencido";
    }
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    if (days > 0) {
      return `${days} d ${hours} h`;
    }
    if (hours > 0) {
      return `${hours} h ${minutes} min`;
    }
    return `${Math.max(minutes, 1)} min`;
  };

  const handleSelectModule = (moduleKey) => {
    setActiveModule(moduleKey);
    setMenuOpen(false);
  };

  const loadProducts = async () => {
    setLoading(true);
    try {
      const data = await getProducts({ limit: 200 });
      setProductos(data);
    } catch (error) {
      toast({
        title: "Error al cargar productos",
        description: "No se pudieron obtener los productos.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const loadVendors = async () => {
    if (!token) return;
    setVendorsLoading(true);
    try {
      const data = await getAdminVendors(token);
      setVendors(Array.isArray(data) ? data : []);
    } catch (error) {
      toast({
        title: "Error al cargar vendedores",
        description: "No se pudo obtener la lista.",
        variant: "destructive",
      });
      if (error.response?.status === 401) {
        handleLogout();
      }
    } finally {
      setVendorsLoading(false);
    }
  };

  const loadVendorTemplates = async () => {
    if (!token) return;
    setVendorTemplatesLoading(true);
    try {
      const data = await getAdminVendorWhatsappTemplates(token);
      setVendorTemplates(Array.isArray(data) ? data : []);
    } catch (error) {
      toast({
        title: "Error al cargar mensajes guardados",
        description: "No se pudieron obtener las plantillas.",
        variant: "destructive",
      });
      if (error.response?.status === 401) {
        handleLogout();
      }
    } finally {
      setVendorTemplatesLoading(false);
    }
  };

  const loadAdminCategories = async () => {
    if (!token) return;
    setCategoriesLoading(true);
    try {
      const data = await getAdminCategories(token);
      setAdminCategories(Array.isArray(data) ? data : []);
    } catch (error) {
      toast({
        title: "Error al cargar categorias",
        description: "No se pudo obtener la lista.",
        variant: "destructive",
      });
      if (error.response?.status === 401) {
        handleLogout();
      }
    } finally {
      setCategoriesLoading(false);
    }
  };

  const loadAdminProductColors = async () => {
    if (!token) return;
    try {
      const data = await getAdminProductColors(token);
      setProductColorOptions(Array.isArray(data) ? data : []);
    } catch (error) {
      if (error.response?.status === 401) {
        handleLogout();
      }
    }
  };

  const loadAdminProductBrands = async () => {
    if (!token) return;
    try {
      const data = await getAdminProductBrands(token);
      setProductBrandOptions(Array.isArray(data) ? data : []);
    } catch (error) {
      if (error.response?.status === 401) {
        handleLogout();
      }
    }
  };

  const loadAdminProductSizes = async (category = form.category) => {
    if (!token) return;
    if (!category || getCategoryTemplateSlug(category) === "accesorios") {
      setProductSizeOptions([]);
      return;
    }
    try {
      const data = await getAdminProductSizes(token, { category });
      setProductSizeOptions(Array.isArray(data) ? data : []);
    } catch (error) {
      if (error.response?.status === 401) {
        handleLogout();
      }
    }
  };

  const loadNewsletterSubscribers = async () => {
    if (!token) return;
    setNewsletterLoading(true);
    try {
      const data = await getAdminNewsletterSubscribers(token);
      setNewsletterSubscribers(Array.isArray(data) ? data : []);
    } catch (error) {
      toast({
        title: "Error al cargar suscriptores",
        description: "No se pudo obtener la lista.",
        variant: "destructive",
      });
      if (error.response?.status === 401) {
        handleLogout();
      }
    } finally {
      setNewsletterLoading(false);
    }
  };

  const loadCoupons = async () => {
    if (!token) return;
    setCouponsLoading(true);
    try {
      const data = await getAdminCoupons(token);
      setCoupons(Array.isArray(data) ? data : []);
    } catch (error) {
      toast({
        title: "Error al cargar cupones",
        description: "No se pudo obtener la lista.",
        variant: "destructive",
      });
      if (error.response?.status === 401) {
        handleLogout();
      }
    } finally {
      setCouponsLoading(false);
    }
  };

  const loadOrders = async () => {
    if (!token) return;
    setOrdersLoading(true);
    try {
      const data = await getAdminOrders(token);
      setOrders(Array.isArray(data) ? data : []);
    } catch (error) {
      toast({
        title: "Error al cargar pedidos",
        description: "No se pudo obtener la lista.",
        variant: "destructive",
      });
      if (error.response?.status === 401) {
        handleLogout();
      }
    } finally {
      setOrdersLoading(false);
    }
  };

  const loadAdminReviews = async () => {
    if (!token) return;
    setReviewsLoading(true);
    try {
      const data = await getAdminReviews(token);
      setReviews(Array.isArray(data) ? data : []);
    } catch (error) {
      toast({
        title: "Error al cargar reseñas",
        description: "No se pudo obtener la lista.",
        variant: "destructive",
      });
      if (error.response?.status === 401) {
        handleLogout();
      }
    } finally {
      setReviewsLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      loadProducts();
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      loadAdminProductColors();
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      loadAdminProductBrands();
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      loadAdminProductSizes(form.category);
    }
  }, [token, form.category]);

  useEffect(() => {
    if (token) {
      loadAdminCategories();
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      loadVendors();
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      loadVendorTemplates();
    }
  }, [token]);

  useEffect(() => {
    if (!vendors.length) {
      setVendorWelcomeForm((prev) => ({ ...prev, vendorId: "" }));
      return;
    }
    const selectedExists = vendors.some(
      (vendor) => String(vendor.id) === String(vendorWelcomeForm.vendorId),
    );
    if (!selectedExists) {
      setVendorWelcomeForm((prev) => ({
        ...prev,
        vendorId: String(vendors[0].id),
      }));
    }
  }, [vendors, vendorWelcomeForm.vendorId]);

  useEffect(() => {
    if (!vendorTemplates.length) {
      setVendorWelcomeForm((prev) => ({ ...prev, templateId: "" }));
      if (editingVendorTemplateId !== null) {
        setEditingVendorTemplateId(null);
        setVendorTemplateForm(createEmptyVendorTemplateForm());
      }
      return;
    }
    const selectedExists = vendorTemplates.some(
      (template) => String(template.id) === String(vendorWelcomeForm.templateId),
    );
    if (!selectedExists && vendorWelcomeForm.templateId) {
      setVendorWelcomeForm((prev) => ({ ...prev, templateId: "" }));
    }
    const editingExists = vendorTemplates.some(
      (template) => String(template.id) === String(editingVendorTemplateId),
    );
    if (!editingExists && editingVendorTemplateId !== null) {
      setEditingVendorTemplateId(null);
      setVendorTemplateForm(createEmptyVendorTemplateForm());
    }
  }, [vendorTemplates, vendorWelcomeForm.templateId, editingVendorTemplateId]);

  useEffect(() => {
    if (token) {
      loadNewsletterSubscribers();
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      loadCoupons();
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      loadOrders();
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      loadAdminReviews();
    }
  }, [token]);

  useEffect(() => {
    if (!token) return;
    setTermsLoading(true);
    getTermsContent()
      .then((data) => {
        setTermsContent(data?.content || "");
      })
      .catch(() => {
        toast({
          title: "Error al cargar terminos",
          description: "No se pudo obtener el contenido.",
          variant: "destructive",
        });
      })
      .finally(() => setTermsLoading(false));
  }, [token, toast]);

  useEffect(() => {
    if (!token) return;
    setPrivacyLoading(true);
    getPrivacyPolicyContent()
      .then((data) => {
        setPrivacyContent(data?.content || "");
      })
      .catch(() => {
        toast({
          title: "Error al cargar politica de privacidad",
          description: "No se pudo obtener el contenido.",
          variant: "destructive",
        });
      })
      .finally(() => setPrivacyLoading(false));
  }, [token, toast]);

  useEffect(() => {
    if (!token) return;
    setPurchaseMessageLoading(true);
    getPurchaseMessageContent()
      .then((data) => {
        setPurchaseMessageContent(data?.content || "");
      })
      .catch(() => {
        toast({
          title: "Error al cargar mensaje final",
          description: "No se pudo obtener el contenido.",
          variant: "destructive",
        });
      })
      .finally(() => setPurchaseMessageLoading(false));
  }, [token, toast]);

  useEffect(() => {
    if (!token) return;
    setHomeSettingsLoading(true);
    getHomeSettings()
      .then((data) => {
        const nextState = buildHomeSettingsState(data);
        setTopBannerText(nextState.topBannerText);
        setCreditPromoText(nextState.creditPromoText);
        setHomeBannerContent(nextState.homeBannerContent);
        setCategoryBannerContent(nextState.categoryBannerContent);
        setHomeImages(nextState.homeImages);
      })
      .catch(() => {
        const nextState = buildHomeSettingsState();
        setTopBannerText(nextState.topBannerText);
        setCreditPromoText(nextState.creditPromoText);
        setHomeBannerContent(nextState.homeBannerContent);
        setCategoryBannerContent(nextState.categoryBannerContent);
        setHomeImages(nextState.homeImages);
      })
      .finally(() => setHomeSettingsLoading(false));
  }, [token]);

  useEffect(() => {
    const currentExists = productCategoryOptions.some(
      (category) => category.value === form.category,
    );
    if (!currentExists && productCategoryOptions.length > 0) {
      const firstActive =
        productCategoryOptions.find((category) => category.is_active) ||
        productCategoryOptions[0];
      setForm((prev) => ({ ...prev, category: firstActive.value }));
    }
  }, [productCategoryOptions, form.category]);

  useEffect(() => {
    if (!isProductFootwear) {
      setSizePairs([]);
      setSizeEditorText("");
    }
  }, [isProductFootwear]);

  useEffect(() => {
    if (!categoryImageFile) {
      setCategoryImagePreview(categoryImageFallback || "");
      return undefined;
    }
    const preview = URL.createObjectURL(categoryImageFile);
    setCategoryImagePreview(preview);
    return () => {
      URL.revokeObjectURL(preview);
    };
  }, [categoryImageFile, categoryImageFallback]);

  useEffect(() => {
    if (!imageFiles.length) {
      setPreviewUrls(editingProduct?.image_url ? [editingProduct.image_url] : []);
      return;
    }
    const previews = imageFiles.map((file) => URL.createObjectURL(file));
    setPreviewUrls(previews);
    return () => {
      previews.forEach((preview) => URL.revokeObjectURL(preview));
    };
  }, [imageFiles, editingProduct]);

  const handleLogin = async (event) => {
    event.preventDefault();
    try {
      const data = await adminLogin(loginForm.username, loginForm.password);
      localStorage.setItem("admin_token", data.access_token);
      setToken(data.access_token);
      toast({ title: "Sesion iniciada" });
    } catch (error) {
      const status = error?.response?.status;
      const detail =
        error?.response?.data?.detail ||
        error?.message ||
        "Error desconocido";
      if (!status) {
        toast({
          title: "No se pudo conectar al backend",
          description: `Verifica que el backend local este activo en ${API_BASE_URL}. Error: ${detail}`,
          variant: "destructive",
        });
        return;
      }
      if (status === 401) {
        toast({
          title: "Credenciales invalidas",
          description: typeof detail === "string" ? detail : "Verifica usuario y clave.",
          variant: "destructive",
        });
        return;
      }
      toast({
        title: `Error de inicio de sesion (${status})`,
        description: typeof detail === "string" ? detail : "No se pudo iniciar sesion.",
        variant: "destructive",
      });
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    setToken("");
    setMenuOpen(false);
    setActiveModule("productos");
    setAdminCategories([]);
    setProductos([]);
    setEditingId(null);
    setVendors([]);
    setVendorTemplates([]);
    setNewsletterSubscribers([]);
    setCoupons([]);
    setOrders([]);
    setReviews([]);
    setPurchaseMessageContent("");
    setProductBrandOptions([]);
    setProductColorOptions([]);
    setProductSizeOptions([]);
    setForm({
      name: "",
      brand: "",
      category: "hombre",
      color: "",
      stock: "",
      whatsappOnly: false,
      price: "",
      salePrice: "",
      description: "",
    });
    setSizePairs([]);
    setSizeEditorText("");
    setImageFiles([]);
    setTopBannerText(DEFAULT_TOP_BANNER_TEXT);
    setCreditPromoText(DEFAULT_CREDIT_PROMO_TEXT);
    setCategoryBannerContent(cloneCategoryBannerContent());
    setHomeImages(EMPTY_HOME_IMAGES);
    setHomeImageFiles({});
    setCouponForm({
      discountType: "percent",
      percent: "5",
      amountOff: "",
      minAmount: "0",
      codePrefix: "ET",
    });
    setVendorForm({
      name: "",
      idNumber: "",
      email: "",
      address: "",
      whatsappPhone: "",
    });
    setVendorWelcomeForm({
      vendorId: "",
      templateId: "",
      message: DEFAULT_VENDOR_WELCOME_MESSAGE,
    });
    setEditingVendorTemplateId(null);
    setVendorTemplateForm(createEmptyVendorTemplateForm());
    setEditingCategoryId(null);
    setCategoryForm(EMPTY_CATEGORY_FORM);
    setCategoryImageFile(null);
    setCategoryImageFallback("");
    setCategoryImagePreview("");
    setCategoryUseSourceImage(false);
    setCategoryImageInputVersion((prev) => prev + 1);
  };

  const handleAddSizePair = () => {
    setSizePairs((prev) => [...prev, createSizePair("", "", "", true)]);
  };

  const handleDuplicateSizePair = (index) => {
    setSizePairs((prev) => {
      const pair = prev[index];
      if (!pair) return prev;
      return [...prev, createSizePair(pair.us, pair.eu, pair.cm, Boolean(pair.selected))];
    });
  };

  const handleSizePairChange = (index, field, value) => {
    setSizePairs((prev) =>
      prev.map((pair, idx) =>
        idx === index ? { ...pair, [field]: value } : pair,
      ),
    );
  };

  const handleRemoveSizePair = (index) => {
    setSizePairs((prev) => prev.filter((_, idx) => idx !== index));
  };

  const handleSizePairSelect = (index, checked) => {
    setSizePairs((prev) =>
      prev.map((pair, idx) =>
        idx === index ? { ...pair, selected: checked } : pair,
      ),
    );
  };

  const handleToggleAllSizePairs = (checked) => {
    setSizePairs((prev) => prev.map((pair) => ({ ...pair, selected: checked })));
  };

  const handleClearSizePairs = () => {
    setSizePairs([]);
    setSizeEditorText("");
  };

  const handleLoadSizePairsIntoEditor = () => {
    setSizeEditorText(formatSizePairsForEditor(sizePairs));
  };

  const handleApplySizeEditor = (mode = "replace") => {
    const parsedPairs = parseSizeEditorPairs(sizeEditorText);
    if (!parsedPairs.length) {
      toast({
        title: "Tallaje invalido",
        description:
          "Escribe una talla por linea. Ejemplo: US 7 / EUR 40 / CM 25.",
        variant: "destructive",
      });
      return;
    }
    if (mode === "append") {
      setSizePairs((prev) => mergeSizePairs(prev, parsedPairs));
      toast({ title: "Tallaje agregado" });
      return;
    }
    setSizePairs(parsedPairs);
    toast({ title: "Tallaje cargado" });
  };

  const handleAppendSuggestedSize = (pair) => {
    if (!pair) return;
    setSizePairs((prev) =>
      mergeSizePairs(prev, [createSizePair(pair.us, pair.eu, pair.cm, true)]),
    );
  };

  const handleDeleteSuggestedSize = async (pair) => {
    const label = formatSizePairLabel(pair);
    if (!label) return;
    if (
      !window.confirm(
        `Deseas borrar la talla "${label}" de la base de datos? Esta accion la quitara de los productos que la tengan guardada en esta categoria.`,
      )
    ) {
      return;
    }
    try {
      const response = await deleteAdminProductSize(token, {
        size: label,
        category: form.category,
      });
      const removedKey = getSizePairKey(pair);
      const nextPairs = sizePairs.filter(
        (currentPair) => getSizePairKey(currentPair) !== removedKey,
      );
      setSizePairs(nextPairs);
      setSizeEditorText(formatSizePairsForEditor(nextPairs));
      await loadProducts();
      await loadAdminProductSizes(form.category);
      toast({
        title: "Talla eliminada",
        description: `Se actualizo ${response?.updated_products || 0} producto(s).`,
      });
    } catch (error) {
      toast({
        title: "Error al eliminar talla",
        description:
          error.response?.data?.detail || "No fue posible borrar la talla guardada.",
        variant: "destructive",
      });
      if (error.response?.status === 401) {
        handleLogout();
      }
    }
  };

  const appendColorToField = (nextColor) => {
    const color = String(nextColor || "").trim();
    if (!color) return;
    setForm((prev) => {
      const values = parseColorTokens(prev.color);
      const exists = values.some(
        (value) => value.toLowerCase() === color.toLowerCase(),
      );
      if (!exists) {
        values.push(color);
      }
      return { ...prev, color: values.join(", ") };
    });
  };

  const setBrandFromSuggestion = (nextBrand) => {
    const brand = String(nextBrand || "").trim();
    if (!brand) return;
    setForm((prev) => ({ ...prev, brand }));
  };

  const handleAppendImageFiles = (event) => {
    const selectedFiles = Array.from(event.target.files || []);
    if (!selectedFiles.length) {
      return;
    }
    const maxImages = isProductFootwear ? 7 : 20;
    setImageFiles((prev) => {
      const allowed = Math.max(0, maxImages - prev.length);
      const nextFiles = selectedFiles.slice(0, allowed);
      if (!nextFiles.length) {
        toast({
          title: "Limite de imagenes",
          description: isProductFootwear
            ? "Para calzado puedes subir maximo 7 imagenes por modelo."
            : "Ya alcanzaste el maximo de imagenes para este producto.",
          variant: "destructive",
        });
        return prev;
      }
      if (selectedFiles.length > nextFiles.length) {
        toast({
          title: "Limite de imagenes",
          description: isProductFootwear
            ? "Para calzado puedes subir maximo 7 imagenes por modelo."
            : "Se agregaron solo las imagenes permitidas.",
        });
      }
      return [...prev, ...nextFiles];
    });
    event.target.value = "";
  };

  const handleRemoveImageFile = (index) => {
    setImageFiles((prev) => prev.filter((_, idx) => idx !== index));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!form.name.trim()) {
      toast({ title: "El nombre es obligatorio", variant: "destructive" });
      return;
    }
    if (!form.brand.trim()) {
      toast({ title: "La marca es obligatoria", variant: "destructive" });
      return;
    }

    const priceValue = parseInt(form.price, 10);
    if (Number.isNaN(priceValue) || priceValue <= 0) {
      toast({ title: "Precio invalido", variant: "destructive" });
      return;
    }

    const stockValue = parseInt(form.stock, 10);
    if (Number.isNaN(stockValue) || stockValue < 0) {
      toast({ title: "Stock invalido", variant: "destructive" });
      return;
    }
    let sizesValue = "";
    if (isProductFootwear) {
      const cleanedPairs = sizePairs
        .filter((pair) => Boolean(pair.selected))
        .map((pair) => ({
          us: normalizeSizeValue(pair.us, "US"),
          eu: normalizeSizeValue(pair.eu, "EUR"),
          cm: normalizeSizeValue(pair.cm, "CM"),
        }))
        .filter((pair) => pair.us || pair.eu || pair.cm);
      if (!cleanedPairs.length) {
        toast({ title: "Las tallas son obligatorias", variant: "destructive" });
        return;
      }
      if (cleanedPairs.some((pair) => !pair.us)) {
        toast({
          title: "Tallaje incompleto",
          description: "Cada fila debe tener al menos el valor US.",
          variant: "destructive",
        });
        return;
      }
      sizesValue = cleanedPairs
        .map((pair) => formatSizePairLabel(pair))
        .filter(Boolean)
        .join(", ");
    }

    const saleValue = form.salePrice ? parseInt(form.salePrice, 10) : null;
    if (form.salePrice && (Number.isNaN(saleValue) || saleValue <= 0)) {
      toast({ title: "Precio en oferta invalido", variant: "destructive" });
      return;
    }
    if (saleValue && saleValue > priceValue) {
      toast({
        title: "Precio en oferta invalido",
        description: "Debe ser menor o igual al precio base.",
        variant: "destructive",
      });
      return;
    }

    if (!editingId && imageFiles.length === 0) {
      toast({ title: "Debes subir al menos una imagen", variant: "destructive" });
      return;
    }
    if (isProductFootwear && imageFiles.length > 7) {
      toast({
        title: "Limite de imagenes",
        description: "Para calzado puedes subir maximo 7 imagenes.",
        variant: "destructive",
      });
      return;
    }

    const payload = new FormData();
    payload.append("name", form.name.trim());
    payload.append("brand", form.brand.trim());
    payload.append("category", form.category);
    payload.append("color", form.color.trim());
    payload.append("price", priceValue.toString());
    payload.append("stock", stockValue.toString());
    payload.append("whatsapp_only", form.whatsappOnly ? "true" : "false");
    if (isProductFootwear) {
      payload.append("sizes", sizesValue);
    }
    if (saleValue) {
      payload.append("sale_price", saleValue.toString());
    }
    if (form.description.trim()) {
      payload.append("description", form.description.trim());
    }
    if (imageFiles.length) {
      imageFiles.forEach((file) => {
        payload.append("images", file);
      });
    }

    try {
      if (editingId) {
        await updateProduct(editingId, payload, token);
      } else {
        await createProduct(payload, token);
      }
      await loadProducts();
      await loadAdminProductBrands();
      await loadAdminProductColors();
      await loadAdminProductSizes(form.category);
      setEditingId(null);
      setForm({
        name: "",
        brand: "",
        category: "hombre",
        color: "",
        stock: "",
        whatsappOnly: false,
        price: "",
        salePrice: "",
        description: "",
      });
      setSizePairs([]);
      setSizeEditorText("");
      setImageFiles([]);
      if (typeof window !== "undefined") {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
      toast({
        title: "Producto guardado",
        description: "El producto fue guardado y el formulario quedo listo para uno nuevo.",
      });
    } catch (error) {
      toast({
        title: "Error al guardar",
        description:
          error.response?.data?.detail || "Verifica los datos y tu sesion.",
        variant: "destructive",
      });
      if (error.response?.status === 401) {
        handleLogout();
      }
    }
  };

  const handleVendorSubmit = async (event) => {
    event.preventDefault();
    if (!vendorForm.name.trim()) {
      toast({ title: "El nombre es obligatorio", variant: "destructive" });
      return;
    }
    if (!vendorForm.idNumber.trim()) {
      toast({ title: "La cedula es obligatoria", variant: "destructive" });
      return;
    }
    if (!vendorForm.email.trim()) {
      toast({ title: "El correo es obligatorio", variant: "destructive" });
      return;
    }
    if (!vendorForm.address.trim()) {
      toast({ title: "La direccion es obligatoria", variant: "destructive" });
      return;
    }
    if (!vendorForm.whatsappPhone.trim()) {
      toast({ title: "El telefono de WhatsApp es obligatorio", variant: "destructive" });
      return;
    }
    setVendorSaving(true);
    try {
      await createVendor(
        {
          name: vendorForm.name.trim(),
          id_number: vendorForm.idNumber.trim(),
          email: vendorForm.email.trim(),
          address: vendorForm.address.trim(),
          whatsapp_phone: vendorForm.whatsappPhone.trim(),
        },
        token,
      );
      toast({ title: "Vendedor creado" });
      setVendorForm({
        name: "",
        idNumber: "",
        email: "",
        address: "",
        whatsappPhone: "",
      });
      await loadVendors();
    } catch (error) {
      toast({
        title: "Error al guardar vendedor",
        description: "Verifica los datos e intenta de nuevo.",
        variant: "destructive",
      });
      if (error.response?.status === 401) {
        handleLogout();
      }
    } finally {
      setVendorSaving(false);
    }
  };

  const handleVendorDelete = async (vendorId) => {
    if (!window.confirm("Deseas eliminar este vendedor?")) {
      return;
    }
    try {
      await deleteVendor(vendorId, token);
      toast({ title: "Vendedor eliminado" });
      await loadVendors();
    } catch (error) {
      const detail =
        error.response?.data?.detail || "No se pudo eliminar el vendedor.";
      toast({
        title: "Error al eliminar vendedor",
        description: detail,
        variant: "destructive",
      });
      if (error.response?.status === 401) {
        handleLogout();
      }
    }
  };

  const handleResetVendorTemplateForm = () => {
    setEditingVendorTemplateId(null);
    setVendorTemplateForm(createEmptyVendorTemplateForm());
  };

  const handleEditVendorTemplate = (template) => {
    if (!template) return;
    setEditingVendorTemplateId(template.id);
    setVendorTemplateForm({
      name: template.name || "",
      message: template.message || DEFAULT_VENDOR_WELCOME_MESSAGE,
    });
  };

  const handleApplyVendorTemplateToWelcomeForm = (template = null) => {
    const templateToUse =
      template ||
      vendorTemplates.find(
        (item) => String(item.id) === String(vendorWelcomeForm.templateId),
      );
    if (!templateToUse) {
      return;
    }
    setVendorWelcomeForm((prev) => ({
      ...prev,
      templateId: String(templateToUse.id),
      message: templateToUse.message || DEFAULT_VENDOR_WELCOME_MESSAGE,
    }));
  };

  const handleVendorTemplateSelectionChange = (value) => {
    const selectedTemplate =
      vendorTemplates.find((template) => String(template.id) === String(value)) || null;
    setVendorWelcomeForm((prev) => ({
      ...prev,
      templateId: value,
      message: selectedTemplate?.message || prev.message,
    }));
  };

  const handleUseCurrentVendorTemplateInWelcomeForm = () => {
    const message = vendorTemplateForm.message.trim();
    if (!message) {
      toast({
        title: "Mensaje obligatorio",
        description: "Escribe un mensaje antes de usarlo en el envio.",
        variant: "destructive",
      });
      return;
    }
    setVendorWelcomeForm((prev) => ({
      ...prev,
      templateId: editingVendorTemplateId ? String(editingVendorTemplateId) : "",
      message,
    }));
    toast({
      title: "Mensaje cargado",
      description: "El mensaje quedo listo en el formulario de envio.",
    });
  };

  const handleVendorTemplateSubmit = async (event) => {
    event.preventDefault();
    const name = vendorTemplateForm.name.trim();
    const message = vendorTemplateForm.message.trim();
    if (!name) {
      toast({
        title: "Nombre obligatorio",
        description: "Escribe un nombre para identificar la plantilla.",
        variant: "destructive",
      });
      return;
    }
    if (!message) {
      toast({
        title: "Mensaje obligatorio",
        description: "Escribe el contenido de la plantilla.",
        variant: "destructive",
      });
      return;
    }

    setVendorTemplateSaving(true);
    try {
      const payload = { name, message };
      const savedTemplate = editingVendorTemplateId
        ? await updateAdminVendorWhatsappTemplate(editingVendorTemplateId, payload, token)
        : await createAdminVendorWhatsappTemplate(payload, token);
      toast({
        title: editingVendorTemplateId ? "Plantilla actualizada" : "Plantilla guardada",
        description: "El mensaje quedo disponible para reutilizarlo cuando quieras.",
      });
      await loadVendorTemplates();
      setEditingVendorTemplateId(savedTemplate?.id || null);
      setVendorTemplateForm({
        name: savedTemplate?.name || name,
        message: savedTemplate?.message || message,
      });
      if (savedTemplate?.id) {
        setVendorWelcomeForm((prev) => ({
          ...prev,
          templateId: String(savedTemplate.id),
          message: savedTemplate.message || prev.message,
        }));
      }
    } catch (error) {
      toast({
        title: "Error al guardar plantilla",
        description:
          error.response?.data?.detail || "No se pudo guardar el mensaje.",
        variant: "destructive",
      });
      if (error.response?.status === 401) {
        handleLogout();
      }
    } finally {
      setVendorTemplateSaving(false);
    }
  };

  const handleDeleteVendorTemplate = async (template) => {
    if (!template?.id) {
      return;
    }
    if (!window.confirm(`Deseas eliminar la plantilla "${template.name}"?`)) {
      return;
    }
    setVendorTemplateDeletingId(template.id);
    try {
      await deleteAdminVendorWhatsappTemplate(template.id, token);
      toast({ title: "Plantilla eliminada" });
      if (String(vendorWelcomeForm.templateId) === String(template.id)) {
        setVendorWelcomeForm((prev) => ({
          ...prev,
          templateId: "",
        }));
      }
      if (String(editingVendorTemplateId) === String(template.id)) {
        handleResetVendorTemplateForm();
      }
      await loadVendorTemplates();
    } catch (error) {
      toast({
        title: "Error al eliminar plantilla",
        description:
          error.response?.data?.detail || "No se pudo eliminar la plantilla.",
        variant: "destructive",
      });
      if (error.response?.status === 401) {
        handleLogout();
      }
    } finally {
      setVendorTemplateDeletingId(null);
    }
  };

  const resetCategoryForm = () => {
    setEditingCategoryId(null);
    setCategoryForm(EMPTY_CATEGORY_FORM);
    setCategoryImageFile(null);
    setCategoryImageFallback("");
    setCategoryUseSourceImage(false);
    setCategoryImageInputVersion((prev) => prev + 1);
  };

  const handleCategoryImageChange = (file) => {
    setCategoryImageFile(file || null);
    if (file) {
      setCategoryUseSourceImage(false);
    }
  };

  const handleEditCategory = (category) => {
    const properties = normalizeCategoryProperties(category.properties);
    setEditingCategoryId(category.id);
    setCategoryForm({
      name: category.name || "",
      slug: category.slug || "",
      description: category.description || "",
      templateSlug: category.template_slug || (category.slug === "accesorios" ? "accesorios" : "hombre"),
      cloneSourceCategoryId: "",
      propertyBrands: formatConfigTokens(properties.brands),
      propertyColors: formatConfigTokens(properties.colors),
      propertySizes: formatConfigTokens(properties.sizes),
      isActive: Boolean(category.is_active),
      sortOrder:
        category.sort_order === 0 || category.sort_order ? String(category.sort_order) : "",
    });
    setCategoryImageFile(null);
    setCategoryImageFallback(category.image_url || "");
    setCategoryUseSourceImage(false);
    setCategoryImageInputVersion((prev) => prev + 1);
  };

  const handleApplyCategoryCloneSource = () => {
    if (!selectedCategoryCloneSource) return;
    const sourceProperties = normalizeCategoryProperties(selectedCategoryCloneSource.properties);
    setCategoryForm((prev) => ({
      ...prev,
      description: selectedCategoryCloneSource.description || prev.description,
      templateSlug:
        selectedCategoryCloneSource.template_slug || prev.templateSlug || "hombre",
      propertyBrands:
        sourceProperties.brands.length > 0
          ? formatConfigTokens(sourceProperties.brands)
          : prev.propertyBrands,
      propertyColors:
        sourceProperties.colors.length > 0
          ? formatConfigTokens(sourceProperties.colors)
          : prev.propertyColors,
      propertySizes:
        sourceProperties.sizes.length > 0
          ? formatConfigTokens(sourceProperties.sizes)
          : prev.propertySizes,
    }));
    if (selectedCategoryCloneSource.image_url) {
      setCategoryImageFile(null);
      setCategoryImageFallback(selectedCategoryCloneSource.image_url);
      setCategoryUseSourceImage(true);
      setCategoryImageInputVersion((prev) => prev + 1);
    }
  };

  const handleCreateCategory = async (event) => {
    event.preventDefault();
    const name = categoryForm.name.trim();
    if (!name) {
      toast({ title: "El nombre es obligatorio", variant: "destructive" });
      return;
    }
    setCategorySaving(true);
    try {
      const propertiesPayload = {
        brands: parseConfigTokens(categoryForm.propertyBrands),
        colors: parseConfigTokens(categoryForm.propertyColors),
        sizes:
          categoryForm.templateSlug === "accesorios"
            ? []
            : splitSizeEditorEntries(categoryForm.propertySizes),
      };
      const payload = new FormData();
      payload.append("name", name);
      payload.append("description", categoryForm.description.trim());
      payload.append("properties_payload", JSON.stringify(propertiesPayload));
      payload.append("template_slug", categoryForm.templateSlug || "hombre");
      payload.append("is_active", categoryForm.isActive ? "true" : "false");
      payload.append(
        "sort_order",
        categoryForm.sortOrder ? String(Number(categoryForm.sortOrder)) : "0",
      );
      if (!editingCategoryId) {
        payload.append("slug", categoryForm.slug.trim());
      }
      if (categoryUseSourceImage && categoryForm.cloneSourceCategoryId) {
        payload.append("clone_source_category_id", String(categoryForm.cloneSourceCategoryId));
      }
      if (categoryUseSourceImage && categoryForm.cloneSourceCategoryId) {
        payload.append("copy_source_image", "true");
      }
      if (categoryImageFile) {
        payload.append("image", categoryImageFile);
      }

      if (editingCategoryId) {
        await updateAdminCategory(editingCategoryId, payload, token);
        toast({ title: "Categoria actualizada" });
      } else {
        await createAdminCategory(payload, token);
        toast({ title: "Categoria creada" });
      }
      resetCategoryForm();
      await loadAdminCategories();
    } catch (error) {
      toast({
        title: editingCategoryId ? "Error al actualizar categoria" : "Error al crear categoria",
        description:
          error.response?.data?.detail ||
          (editingCategoryId
            ? "No fue posible actualizar la categoria."
            : "No fue posible crear la categoria."),
        variant: "destructive",
      });
      if (error.response?.status === 401) {
        handleLogout();
      }
    } finally {
      setCategorySaving(false);
    }
  };

  const handleToggleCategoryStatus = async (category) => {
    try {
      const payload = new FormData();
      payload.append("is_active", !category.is_active ? "true" : "false");
      await updateAdminCategory(category.id, payload, token);
      toast({
        title: category.is_active ? "Categoria desactivada" : "Categoria activada",
      });
      await loadAdminCategories();
    } catch (error) {
      toast({
        title: "Error al actualizar categoria",
        description: error.response?.data?.detail || "No fue posible actualizar.",
        variant: "destructive",
      });
      if (error.response?.status === 401) {
        handleLogout();
      }
    }
  };

  const handleDeleteCategory = async (category) => {
    if (!window.confirm(`Deseas eliminar la categoria "${category.name}"?`)) {
      return;
    }
    try {
      await deleteAdminCategory(category.id, token);
      toast({ title: "Categoria eliminada" });
      await loadAdminCategories();
    } catch (error) {
      toast({
        title: "Error al eliminar categoria",
        description: error.response?.data?.detail || "No fue posible eliminar.",
        variant: "destructive",
      });
      if (error.response?.status === 401) {
        handleLogout();
      }
    }
  };

  const handleSendVendorWelcomeEmail = async (event) => {
    event.preventDefault();
    const vendorId = Number(vendorWelcomeForm.vendorId);
    if (!vendorId) {
      toast({
        title: "Selecciona un vendedor",
        description: "Debes elegir un vendedor para enviar el mensaje.",
        variant: "destructive",
      });
      return;
    }
    const message = vendorWelcomeForm.message.trim();
    if (!message) {
      toast({
        title: "Mensaje obligatorio",
        description: "Escribe el mensaje de bienvenida.",
        variant: "destructive",
      });
      return;
    }

    setVendorWelcomeSending(true);
    try {
      await sendVendorWelcomeEmail(
        {
          vendor_id: vendorId,
          message,
        },
        token,
      );
      toast({
        title: "Correo enviado",
        description: "El mensaje de bienvenida fue enviado correctamente por correo.",
      });
    } catch (error) {
      toast({
        title: "Error al enviar correo",
        description:
          error.response?.data?.detail || "No se pudo enviar el mensaje.",
        variant: "destructive",
      });
      if (error.response?.status === 401) {
        handleLogout();
      }
    } finally {
      setVendorWelcomeSending(false);
    }
  };

  const handleCouponSubmit = async (event) => {
    event.preventDefault();
    const discountType = couponForm.discountType;
    const rawPercent = Number(couponForm.percent);
    const rawAmountOff = Number(couponForm.amountOff);
    const minAmountValue = Number(couponForm.minAmount || 0);
    if (Number.isNaN(minAmountValue) || minAmountValue < 0) {
      toast({
        title: "Monto minimo invalido",
        description: "El monto minimo no puede ser negativo.",
        variant: "destructive",
      });
      return;
    }
    if (discountType === "percent") {
      if (Number.isNaN(rawPercent) || rawPercent <= 0) {
        toast({
          title: "Porcentaje invalido",
          description: "Ingresa un porcentaje mayor a 0.",
          variant: "destructive",
        });
        return;
      }
    } else {
      if (Number.isNaN(rawAmountOff) || rawAmountOff <= 0) {
        toast({
          title: "Monto fijo invalido",
          description: "Ingresa un valor fijo mayor a 0.",
          variant: "destructive",
        });
        return;
      }
    }
    setCouponSaving(true);
    try {
      const payload =
        discountType === "percent"
          ? {
              percent: rawPercent,
              min_amount: Math.floor(minAmountValue),
              code_prefix: couponForm.codePrefix || "ET",
            }
          : {
              amount_off: Math.floor(rawAmountOff),
              min_amount: Math.floor(minAmountValue),
              code_prefix: couponForm.codePrefix || "ET",
            };
      await createAdminCoupon(
        payload,
        token,
      );
      toast({
        title: "Cupon creado",
        description: "Se genero un codigo unico correctamente.",
      });
      setCouponForm((prev) => ({
        ...prev,
        discountType: prev.discountType || "percent",
        percent: prev.percent || "5",
        amountOff: prev.amountOff || "",
        minAmount: prev.minAmount || "0",
      }));
      await loadCoupons();
    } catch (error) {
      toast({
        title: "Error al crear cupon",
        description: error.response?.data?.detail || "No fue posible crear el cupon.",
        variant: "destructive",
      });
      if (error.response?.status === 401) {
        handleLogout();
      }
    } finally {
      setCouponSaving(false);
    }
  };

  const handleDisableCoupon = async (couponId) => {
    if (!window.confirm("Deseas desactivar este cupon?")) {
      return;
    }
    try {
      await disableAdminCoupon(couponId, token);
      toast({ title: "Cupon desactivado" });
      await loadCoupons();
    } catch (error) {
      toast({
        title: "Error al desactivar cupon",
        description: error.response?.data?.detail || "No fue posible desactivar el cupon.",
        variant: "destructive",
      });
      if (error.response?.status === 401) {
        handleLogout();
      }
    }
  };

  const handleEdit = (product) => {
    setEditingId(product.id);
    const targetCategory = product.category || "hombre";
    const targetTemplate = getCategoryTemplateSlug(targetCategory);
    const normalizedSizePairs =
      targetTemplate === "accesorios"
        ? []
        : coerceSizePairs(product.sizes);
    setForm({
      name: product.name || "",
      brand: product.brand || "",
      category: targetCategory,
      color: product.color || "",
      stock: product.stock?.toString() || "",
      whatsappOnly: Boolean(product.whatsapp_only),
      price: product.price?.toString() || "",
      salePrice: product.sale_price?.toString() || "",
      description: product.description || "",
    });
    const nextSizePairs = normalizedSizePairs;
    setSizePairs(nextSizePairs);
    setSizeEditorText(formatSizePairsForEditor(nextSizePairs));
    setImageFiles([]);
  };

  const handleDelete = async (productId) => {
    if (!window.confirm("Deseas eliminar este producto?")) {
      return;
    }
    try {
      await deleteProduct(productId, token);
      toast({ title: "Producto eliminado" });
      await loadProducts();
    } catch (error) {
      toast({
        title: "Error al eliminar",
        description: "Intenta nuevamente.",
        variant: "destructive",
      });
      if (error.response?.status === 401) {
        handleLogout();
      }
    }
  };

  const handleSaveTerms = async (event) => {
    event.preventDefault();
    if (!termsContent.trim()) {
      toast({ title: "El contenido es obligatorio", variant: "destructive" });
      return;
    }
    setTermsSaving(true);
    try {
      await updateTermsContent(termsContent, token);
      toast({ title: "Terminos actualizados" });
    } catch (error) {
      toast({
        title: "Error al guardar terminos",
        description: "Verifica tu sesion e intenta de nuevo.",
        variant: "destructive",
      });
      if (error.response?.status === 401) {
        handleLogout();
      }
    } finally {
      setTermsSaving(false);
    }
  };

  const handleSavePrivacy = async (event) => {
    event.preventDefault();
    if (!privacyContent.trim()) {
      toast({ title: "El contenido es obligatorio", variant: "destructive" });
      return;
    }
    setPrivacySaving(true);
    try {
      await updatePrivacyPolicyContent(privacyContent, token);
      toast({ title: "Politica de privacidad actualizada" });
    } catch (error) {
      toast({
        title: "Error al guardar politica de privacidad",
        description: "Verifica tu sesion e intenta de nuevo.",
        variant: "destructive",
      });
      if (error.response?.status === 401) {
        handleLogout();
      }
    } finally {
      setPrivacySaving(false);
    }
  };

  const handleHomeImageFile = (field, file) => {
    setHomeImageFiles((prev) => ({ ...prev, [field]: file || null }));
  };

  const handleHomeBannerContentChange = (bannerKey, field, value) => {
    setHomeBannerContent((prev) => {
      const current = prev?.[bannerKey] || normalizeHomeBannerEntry();
      const nextValue =
        field === "steps"
          ? String(value || "")
              .split(/\r?\n/)
              .map((item) => item.trim())
              .filter(Boolean)
          : value;
      return {
        ...prev,
        [bannerKey]: {
          ...current,
          [field]: nextValue,
        },
      };
    });
  };

  const handleHomeCategoryBannerChange = (categoryKey, field, value) => {
    setCategoryBannerContent((prev) => {
      const current = prev?.[categoryKey] || normalizeCategoryBannerEntry();
      return {
        ...prev,
        [categoryKey]: {
          ...current,
          [field]: value,
        },
      };
    });
  };

  const handleSaveHomeSettings = async (event) => {
    event.preventDefault();
    const cleanedText = topBannerText.trim();
    const cleanedPromoText = creditPromoText.trim();
    if (!cleanedText) {
      toast({
        title: "Texto superior obligatorio",
        variant: "destructive",
      });
      return;
    }
    if (!cleanedPromoText) {
      toast({
        title: "Texto del banner de credito obligatorio",
        variant: "destructive",
      });
      return;
    }
    const cleanedHeroBanners = {};
    for (const field of HERO_IMAGE_FIELDS) {
      const current = normalizeHomeBannerEntry(
        homeBannerContent?.[field.key],
        DEFAULT_HOME_BANNER_CONTENT[field.key],
      );
      if (!current.title || !current.subtitle || !current.cta || !current.link) {
        toast({
          title: `Completa el contenido de ${field.label}`,
          description: "Cada banner debe tener titulo, subtitulo, boton y enlace.",
          variant: "destructive",
        });
        return;
      }
      cleanedHeroBanners[field.key] = current;
    }
    const cleanedCategoryBanners = {};
    for (const field of CATEGORY_IMAGE_FIELDS) {
      cleanedCategoryBanners[field.key] = normalizeCategoryBannerEntry(
        categoryBannerContent?.[field.key],
        DEFAULT_CATEGORY_BANNER_CONTENT[field.key],
      );
    }
    const payload = new FormData();
    payload.append("top_banner_text", cleanedText);
    payload.append("credit_promo_text", cleanedPromoText);
    payload.append("hero_banners_payload", JSON.stringify(cleanedHeroBanners));
    payload.append("category_banners_payload", JSON.stringify(cleanedCategoryBanners));

    if (homeImageFiles.banner_1) payload.append("hero_image_1", homeImageFiles.banner_1);
    if (homeImageFiles.banner_2) payload.append("hero_image_2", homeImageFiles.banner_2);
    if (homeImageFiles.banner_3) payload.append("hero_image_3", homeImageFiles.banner_3);
    if (homeImageFiles.banner_4) payload.append("hero_image_4", homeImageFiles.banner_4);
    if (homeImageFiles.hombre) {
      payload.append("category_image_hombre", homeImageFiles.hombre);
    }
    if (homeImageFiles.mujer) {
      payload.append("category_image_mujer", homeImageFiles.mujer);
    }
    if (homeImageFiles.accesorios) {
      payload.append("category_image_accesorios", homeImageFiles.accesorios);
    }
    for (let index = 1; index <= 10; index += 1) {
      const key = `payment_${index}`;
      if (homeImageFiles[key]) {
        payload.append(`payment_image_${index}`, homeImageFiles[key]);
      }
    }

    setHomeSettingsSaving(true);
    try {
      const response = await updateHomeSettings(payload, token);
      const settings = response?.settings || {};
      const nextState = buildHomeSettingsState(
        settings,
        homeImages,
        cleanedText,
        cleanedPromoText,
        cleanedHeroBanners,
        cleanedCategoryBanners,
      );
      setTopBannerText(nextState.topBannerText);
      setCreditPromoText(nextState.creditPromoText);
      setHomeBannerContent(nextState.homeBannerContent);
      setCategoryBannerContent(nextState.categoryBannerContent);
      setHomeImages(nextState.homeImages);
      setHomeImageFiles({});
      setHomeImageInputVersion((prev) => prev + 1);
      toast({ title: "Configuracion de home actualizada" });
    } catch (error) {
      toast({
        title: "Error al guardar home",
        description: "Verifica los archivos e intenta de nuevo.",
        variant: "destructive",
      });
      if (error.response?.status === 401) {
        handleLogout();
      }
    } finally {
      setHomeSettingsSaving(false);
    }
  };

  const handleSavePurchaseMessage = async (event) => {
    event.preventDefault();
    const content = purchaseMessageContent.trim();
    if (!content) {
      toast({
        title: "Mensaje obligatorio",
        description: "Debes escribir el mensaje final para el comprador.",
        variant: "destructive",
      });
      return;
    }
    setPurchaseMessageSaving(true);
    try {
      await updatePurchaseMessageContent(content, token);
      toast({ title: "Mensaje final actualizado" });
    } catch (error) {
      toast({
        title: "Error al guardar mensaje",
        description: error.response?.data?.detail || "No fue posible actualizar.",
        variant: "destructive",
      });
      if (error.response?.status === 401) {
        handleLogout();
      }
    } finally {
      setPurchaseMessageSaving(false);
    }
  };

  const handleDeleteReview = async (reviewId) => {
    if (!window.confirm("Deseas eliminar esta resena?")) {
      return;
    }
    try {
      await deleteAdminReview(reviewId, token);
      toast({ title: "Resena eliminada" });
      await loadAdminReviews();
    } catch (error) {
      toast({
        title: "Error al eliminar resena",
        description: error.response?.data?.detail || "No fue posible eliminar.",
        variant: "destructive",
      });
      if (error.response?.status === 401) {
        handleLogout();
      }
    }
  };

  const isFootwear = isProductFootwear;

  if (!token) {
    return (
      <div className="min-h-screen bg-gray-50 py-16">
        <Seo title="Admin" robots="noindex, nofollow" />
        <div className="container mx-auto px-4 max-w-md">
          <Card>
            <CardHeader>
              <CardTitle>Administrador</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleLogin} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="admin-username">Usuario</Label>
                  <Input
                    id="admin-username"
                    value={loginForm.username}
                    onChange={(event) =>
                      setLoginForm((prev) => ({ ...prev, username: event.target.value }))
                    }
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="admin-password">Contrasena</Label>
                  <Input
                    id="admin-password"
                    type="password"
                    value={loginForm.password}
                    onChange={(event) =>
                      setLoginForm((prev) => ({ ...prev, password: event.target.value }))
                    }
                    required
                  />
                </div>
                <Button type="submit" className="w-full">
                  Iniciar sesion
                </Button>
                <Button type="button" asChild variant="outline" className="w-full">
                  <Link to="/admin-seguridad">Seguridad y cambio de clave</Link>
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-10">
      <Seo title="Admin" robots="noindex, nofollow" />
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold">Panel de Administracion</h1>
            <p className="text-gray-600 text-sm">
              Gestiona productos de hombre, mujer y accesorios.
            </p>
            <p className="text-xs text-gray-500 mt-1">
              Modulo activo:{" "}
              {ADMIN_MODULES.find((module) => module.key === activeModule)?.label || "Productos"}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button asChild type="button" variant="outline">
              <Link to="/admin-seguridad">Seguridad</Link>
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => setMenuOpen((prev) => !prev)}
            >
              Modulos
            </Button>
            <Button variant="outline" onClick={handleLogout}>
              Cerrar sesion
            </Button>
          </div>
        </div>

        {menuOpen && (
          <Card className="mb-8">
            <CardContent className="pt-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {ADMIN_MODULES.map((module) => (
                  <Button
                    key={module.key}
                    type="button"
                    variant={activeModule === module.key ? "default" : "outline"}
                    className="justify-start"
                    onClick={() => handleSelectModule(module.key)}
                  >
                    {module.label}
                  </Button>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        <div className="grid grid-cols-1 gap-8 max-w-6xl mx-auto">
          <div className={`lg:col-span-1 ${activeModule === "pedidos" ? "hidden" : ""}`}>
            <Card className={activeModule === "productos" ? "" : "hidden"}>
              <CardHeader>
                <CardTitle>{editingId ? "Editar producto" : "Nuevo producto"}</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="product-name">Nombre</Label>
                    <Input
                      id="product-name"
                      value={form.name}
                      onChange={(event) =>
                        setForm((prev) => ({ ...prev, name: event.target.value }))
                      }
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="product-brand">Marca</Label>
                    <Input
                      id="product-brand"
                      list="product-brand-presets"
                      value={form.brand}
                      onChange={(event) =>
                        setForm((prev) => ({ ...prev, brand: event.target.value }))
                      }
                      placeholder="Ej: Nike"
                      required
                    />
                    <datalist id="product-brand-presets">
                      {filteredBrandOptions.map((brand) => (
                        <option key={brand} value={brand} />
                      ))}
                    </datalist>
                    {filteredBrandOptions.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {filteredBrandOptions.slice(0, 12).map((brand) => (
                          <Button
                            key={`preset-brand-${brand}`}
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => setBrandFromSuggestion(brand)}
                          >
                            {brand}
                          </Button>
                        ))}
                      </div>
                    )}
                    <p className="text-xs text-gray-500">
                      Puedes seleccionar una marca sugerida o escribir una nueva. Las marcas usadas quedan disponibles para futuras cargas.
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label>Categoria</Label>
                    <Select
                      value={form.category}
                      onValueChange={(value) =>
                        setForm((prev) => ({ ...prev, category: value }))
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecciona categoria" />
                      </SelectTrigger>
                      <SelectContent>
                        {productCategoryOptions.map((category) => (
                          <SelectItem key={category.value} value={category.value}>
                            {category.label}
                            {!category.is_active && category.value !== "unisex"
                              ? " (Inactiva)"
                              : ""}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="product-color">Color (opcional)</Label>
                    <Input
                      id="product-color"
                      list="product-color-presets"
                      value={form.color}
                      onChange={(event) =>
                        setForm((prev) => ({ ...prev, color: event.target.value }))
                      }
                      placeholder="Ej: Negro, Blanco"
                    />
                    <datalist id="product-color-presets">
                      {filteredColorOptions.map((color) => (
                        <option key={color} value={color} />
                      ))}
                    </datalist>
                    {filteredColorOptions.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {filteredColorOptions.slice(0, 12).map((color) => (
                          <Button
                            key={`preset-color-${color}`}
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => appendColorToField(color)}
                          >
                            {color}
                          </Button>
                        ))}
                      </div>
                    )}
                    <p className="text-xs text-gray-500">
                      Puedes escribir varios colores separados por coma. Los colores usados quedan sugeridos para futuras cargas.
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="product-stock">Stock</Label>
                    <Input
                      id="product-stock"
                      type="number"
                      min="0"
                      value={form.stock}
                      onChange={(event) =>
                        setForm((prev) => ({ ...prev, stock: event.target.value }))
                      }
                      required
                    />
                  </div>
                  <div className="flex items-center gap-2 rounded border border-gray-200 px-3 py-2">
                    <input
                      id="product-whatsapp-only"
                      type="checkbox"
                      checked={form.whatsappOnly}
                      onChange={(event) =>
                        setForm((prev) => ({
                          ...prev,
                          whatsappOnly: event.target.checked,
                        }))
                      }
                    />
                    <Label htmlFor="product-whatsapp-only" className="text-sm">
                      Compra solo por WhatsApp (desactiva pagos en la tienda)
                    </Label>
                  </div>
                  {isFootwear && (
                    <div className="space-y-2">
                      <Label>Tallas (US / EUR / CM)</Label>
                      <div className="space-y-2">
                        {sizePairs.length === 0 && (
                          <p className="text-xs text-gray-500">
                            Agrega las tallas que quieres mostrar en la tienda.
                          </p>
                        )}
                        {sizePairs.length > 0 && (
                          <p className="text-xs text-gray-500">
                            Puedes editar cualquier valor directamente y marcar con el checkbox las tallas disponibles para este modelo.
                          </p>
                        )}
                        {sizePairs.map((pair, index) => (
                          <div
                            key={`size-pair-${index}`}
                            className="grid gap-2 sm:grid-cols-[auto_1fr_1fr_1fr_auto_auto]"
                          >
                            <label className="flex items-center justify-center px-2">
                              <input
                                type="checkbox"
                                checked={Boolean(pair.selected)}
                                onChange={(event) =>
                                  handleSizePairSelect(index, event.target.checked)
                                }
                              />
                            </label>
                            <Input
                              value={pair.us}
                              onChange={(event) =>
                                handleSizePairChange(index, "us", event.target.value)
                              }
                              placeholder="US"
                            />
                            <Input
                              value={pair.eu}
                              onChange={(event) =>
                                handleSizePairChange(index, "eu", event.target.value)
                              }
                              placeholder="EUR"
                            />
                            <Input
                              value={pair.cm}
                              onChange={(event) =>
                                handleSizePairChange(index, "cm", event.target.value)
                              }
                              placeholder="CM"
                            />
                            <Button
                              type="button"
                              variant="outline"
                              onClick={() => handleDuplicateSizePair(index)}
                            >
                              Duplicar
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              onClick={() => handleRemoveSizePair(index)}
                            >
                              Quitar
                            </Button>
                          </div>
                        ))}
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <Button type="button" variant="outline" onClick={handleAddSizePair}>
                          Agregar talla
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => handleToggleAllSizePairs(true)}
                          disabled={sizePairs.length === 0}
                        >
                          Seleccionar todas
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => handleToggleAllSizePairs(false)}
                          disabled={sizePairs.length === 0}
                        >
                          Deseleccionar todas
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={handleClearSizePairs}
                          disabled={sizePairs.length === 0}
                        >
                          Limpiar tallas
                        </Button>
                      </div>
                      <div className="space-y-2 rounded-lg border border-gray-200 bg-gray-50 p-3">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <Label>Tallas guardadas</Label>
                          <span className="text-xs text-gray-500">
                            Se alimentan con los productos ya guardados en esta categoria.
                          </span>
                        </div>
                        {suggestedSizePairs.length > 0 ? (
                          <div className="flex flex-wrap gap-2">
                            {suggestedSizePairs.map((pair, index) => {
                              const label = formatSizePairLabel(pair);
                              const key = getSizePairKey(pair) || `${label}-${index}`;
                              const alreadyAdded = currentSizePairKeys.has(key);
                              return (
                                <div
                                  key={key}
                                  className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white p-2"
                                >
                                  <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => handleAppendSuggestedSize(pair)}
                                    disabled={alreadyAdded}
                                    className="h-auto whitespace-normal py-2 text-left"
                                  >
                                    {alreadyAdded ? `${label} agregada` : label}
                                  </Button>
                                  <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => handleDeleteSuggestedSize(pair)}
                                    className="text-red-600 hover:text-red-700"
                                  >
                                    Borrar
                                  </Button>
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <p className="text-xs text-gray-500">
                            Aun no hay tallas guardadas para esta categoria. Las que registres
                            aqui quedaran disponibles como sugerencia en futuros ingresos.
                          </p>
                        )}
                      </div>
                      <div className="space-y-2 rounded-lg border border-gray-200 bg-gray-50 p-3">
                        <Label htmlFor="product-size-editor">
                          Tallaje completo editable
                        </Label>
                        <Textarea
                          id="product-size-editor"
                          rows={6}
                          value={sizeEditorText}
                          onChange={(event) => setSizeEditorText(event.target.value)}
                          placeholder={
                            "US 7 / EUR 40 / CM 25\nUS 7.5 / EUR 40.5 / CM 25.5\nUS 8 / EUR 41 / CM 26"
                          }
                        />
                        <div className="flex flex-wrap gap-2">
                          <Button
                            type="button"
                            variant="outline"
                            onClick={handleLoadSizePairsIntoEditor}
                            disabled={sizePairs.length === 0}
                          >
                            Copiar tabla al editor
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => handleApplySizeEditor("replace")}
                            disabled={!sizeEditorText.trim()}
                          >
                            Reemplazar tabla
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => handleApplySizeEditor("append")}
                            disabled={!sizeEditorText.trim()}
                          >
                            Agregar al listado
                          </Button>
                        </div>
                        <p className="text-xs text-gray-500">
                          Escribe una talla por linea. Puedes usar formatos como
                          {" "}
                          <span className="font-mono">US 7 / EUR 40 / CM 25</span>,
                          {" "}
                          <span className="font-mono">US 7 / EUR 40</span>
                          {" "}o{" "}
                          <span className="font-mono">7 / 40 / 25</span>.
                        </p>
                      </div>
                      <p className="text-xs text-gray-500">
                        Define y edita libremente las combinaciones exactas de US, EUR y CM.
                        Ya no se cargan tablas predefinidas: el tallaje se va poblando con lo que
                        guardes desde el admin y luego podras reutilizarlo o editarlo cuando
                        quieras.
                      </p>
                    </div>
                  )}
                  <div className="space-y-2">
                    <Label htmlFor="product-price">Precio</Label>
                    <Input
                      id="product-price"
                      type="number"
                      min="1"
                      value={form.price}
                      onChange={(event) =>
                        setForm((prev) => ({ ...prev, price: event.target.value }))
                      }
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="product-sale-price">Precio en oferta (opcional)</Label>
                    <Input
                      id="product-sale-price"
                      type="number"
                      min="1"
                      value={form.salePrice}
                      onChange={(event) =>
                        setForm((prev) => ({ ...prev, salePrice: event.target.value }))
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="product-description">Descripcion (opcional)</Label>
                    <Textarea
                      id="product-description"
                      rows={3}
                      value={form.description}
                      onChange={(event) =>
                        setForm((prev) => ({ ...prev, description: event.target.value }))
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="product-image-input">
                      {editingId ? "Actualizar imagenes (opcional)" : "Imagenes"}
                    </Label>
                    <input
                      id="product-image-input"
                      ref={imageInputRef}
                      type="file"
                      accept="image/*"
                      multiple
                      className="hidden"
                      onChange={handleAppendImageFiles}
                    />
                    <div className="flex flex-wrap gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => imageInputRef.current?.click()}
                      >
                        Agregar imagen
                      </Button>
                      {imageFiles.length > 0 && (
                        <Button
                          type="button"
                          variant="ghost"
                          onClick={() => setImageFiles([])}
                        >
                          Limpiar imagenes
                        </Button>
                      )}
                    </div>
                    {imageFiles.length === 0 && (
                      <p className="text-xs text-gray-500">
                        Aun no hay imagenes nuevas seleccionadas.
                      </p>
                    )}
                    <p className="text-xs text-gray-500">
                      Agrega una o varias imagenes por clic. En calzado puedes subir maximo
                      7 fotos por modelo. Si actualizas, se reemplazan las anteriores al guardar.
                    </p>
                  </div>
                  {previewUrls.length > 0 && (
                    <div className="grid gap-3 sm:grid-cols-2">
                      {previewUrls.map((preview, index) => (
                        <div
                          key={`${preview}-${index}`}
                          className="rounded-lg overflow-hidden border bg-white"
                        >
                          <img
                            src={preview}
                            alt={`Preview ${index + 1}`}
                            className="w-full h-40 object-cover"
                          />
                          <div className="space-y-1 p-2">
                            <p className="text-xs text-gray-500 truncate">
                              {imageFiles[index]?.name || `Imagen ${index + 1}`}
                            </p>
                            <p className="text-xs text-gray-500">
                              Foto {index + 1} del modelo.
                            </p>
                            {imageFiles[index] && (
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="w-full"
                                onClick={() => handleRemoveImageFile(index)}
                              >
                                Quitar imagen
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="flex gap-2">
                    <Button type="submit" className="flex-1">
                      {editingId ? "Actualizar" : "Guardar"}
                    </Button>
                    {editingId && (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          setEditingId(null);
                          setForm({
                            name: "",
                            brand: "",
                            category: "hombre",
                            color: "",
                            stock: "",
                            whatsappOnly: false,
                            price: "",
                            salePrice: "",
                            description: "",
                          });
                          setSizePairs([]);
                          setSizeEditorText("");
                          setImageFiles([]);
                        }}
                      >
                        Cancelar
                      </Button>
                    )}
                  </div>
                </form>
              </CardContent>
            </Card>
            <Card className={`mt-6 ${activeModule === "categorias" ? "" : "hidden"}`}>
              <CardHeader>
                <CardTitle>Categorias</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <form onSubmit={handleCreateCategory} className="space-y-3">
                  <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 px-3 py-3 text-xs text-gray-600">
                    Crea categorias nuevas a tu gusto eligiendo una plantilla base.
                    La plantilla define si la categoria funcionara como Hombre, Mujer o
                    Accesorios al publicar productos.
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="category-name">Nombre</Label>
                    <Input
                      id="category-name"
                      value={categoryForm.name}
                      onChange={(event) =>
                        setCategoryForm((prev) => ({ ...prev, name: event.target.value }))
                      }
                      placeholder="Ej: Infantil"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="category-slug">Slug (opcional)</Label>
                    <Input
                      id="category-slug"
                      value={categoryForm.slug}
                      onChange={(event) =>
                        setCategoryForm((prev) => ({ ...prev, slug: event.target.value }))
                      }
                      placeholder="ej: infantil"
                      disabled={Boolean(editingCategoryId)}
                    />
                    {editingCategoryId && (
                      <p className="text-xs text-gray-500">
                        El slug se deja fijo al editar para no romper productos ni rutas.
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="category-template">Plantilla base</Label>
                    <Select
                      value={categoryForm.templateSlug}
                      onValueChange={(value) =>
                        setCategoryForm((prev) => ({ ...prev, templateSlug: value }))
                      }
                    >
                      <SelectTrigger id="category-template">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {CATEGORY_TEMPLATE_OPTIONS.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-gray-500">
                      Elige los valores base que necesitas para esta categoria nueva.
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="category-clone-source">Copiar contenido desde otra categoria</Label>
                    <Select
                      value={categoryForm.cloneSourceCategoryId || "none"}
                      onValueChange={(value) =>
                        setCategoryForm((prev) => ({
                          ...prev,
                          cloneSourceCategoryId: value === "none" ? "" : value,
                        }))
                      }
                    >
                      <SelectTrigger id="category-clone-source">
                        <SelectValue placeholder="Opcional" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No copiar</SelectItem>
                        {adminCategories
                          .filter((category) => !editingCategoryId || category.id !== editingCategoryId)
                          .map((category) => (
                            <SelectItem key={category.id} value={String(category.id)}>
                              {category.name}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={!selectedCategoryCloneSource}
                        onClick={handleApplyCategoryCloneSource}
                      >
                        Copiar texto, imagen y propiedades
                      </Button>
                      {selectedCategoryCloneSource && (
                        <p className="text-xs text-gray-500 self-center">
                          Usa su descripcion, imagen y presets como punto de partida para tu nueva categoria.
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="category-description">Texto / descripcion</Label>
                    <Textarea
                      id="category-description"
                      rows={3}
                      value={categoryForm.description}
                      onChange={(event) =>
                        setCategoryForm((prev) => ({
                          ...prev,
                          description: event.target.value,
                        }))
                      }
                      placeholder="Texto descriptivo para mostrar en la pagina."
                    />
                  </div>
                  <div className="grid gap-3 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="category-property-brands">Marcas sugeridas</Label>
                      <Textarea
                        id="category-property-brands"
                        rows={4}
                        value={categoryForm.propertyBrands}
                        onChange={(event) =>
                          setCategoryForm((prev) => ({
                            ...prev,
                            propertyBrands: event.target.value,
                          }))
                        }
                        placeholder="Nike&#10;Adidas&#10;Puma"
                      />
                      <p className="text-xs text-gray-500">
                        Una por linea o separadas por coma.
                      </p>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="category-property-colors">Colores sugeridos</Label>
                      <Textarea
                        id="category-property-colors"
                        rows={4}
                        value={categoryForm.propertyColors}
                        onChange={(event) =>
                          setCategoryForm((prev) => ({
                            ...prev,
                            propertyColors: event.target.value,
                          }))
                        }
                        placeholder="Negro&#10;Blanco&#10;Rojo"
                      />
                      <p className="text-xs text-gray-500">
                        Serviran como guia rapida al crear productos.
                      </p>
                    </div>
                  </div>
                  {categoryForm.templateSlug !== "accesorios" && (
                    <div className="space-y-2">
                      <Label htmlFor="category-property-sizes">Tallas base</Label>
                      <Textarea
                        id="category-property-sizes"
                        rows={5}
                        value={categoryForm.propertySizes}
                        onChange={(event) =>
                          setCategoryForm((prev) => ({
                            ...prev,
                            propertySizes: event.target.value,
                          }))
                        }
                        placeholder="US 7 / EUR 40 / CM 25&#10;US 8 / EUR 41 / CM 26"
                      />
                      <p className="text-xs text-gray-500">
                        Una talla por linea. Si copias una categoria base, aqui quedara su tallaje sugerido.
                      </p>
                    </div>
                  )}
                  <div className="space-y-2">
                    <Label htmlFor="category-image">Imagen de categoria</Label>
                    {categoryImagePreview && (
                      <img
                        src={categoryImagePreview}
                        alt={categoryForm.name || "Vista previa de categoria"}
                        className="h-28 w-full rounded border object-cover"
                      />
                    )}
                    <Input
                      key={`category-image-${categoryImageInputVersion}`}
                      id="category-image"
                      type="file"
                      accept="image/*"
                      onChange={(event) => handleCategoryImageChange(event.target.files?.[0] || null)}
                    />
                    {categoryUseSourceImage && !categoryImageFile && (
                      <p className="text-xs text-gray-500">
                        Se usara la imagen copiada desde la categoria base si no subes una nueva.
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="category-sort-order">Orden (opcional)</Label>
                    <Input
                      id="category-sort-order"
                      type="number"
                      min="0"
                      value={categoryForm.sortOrder}
                      onChange={(event) =>
                        setCategoryForm((prev) => ({ ...prev, sortOrder: event.target.value }))
                      }
                    />
                  </div>
                  <div className="flex items-center gap-2 rounded border border-gray-200 px-3 py-2">
                    <input
                      id="category-active"
                      type="checkbox"
                      checked={categoryForm.isActive}
                      onChange={(event) =>
                        setCategoryForm((prev) => ({
                          ...prev,
                          isActive: event.target.checked,
                        }))
                      }
                    />
                    <Label htmlFor="category-active" className="text-sm">
                      Categoria activa
                    </Label>
                  </div>
                  <div className="flex gap-2">
                    <Button type="submit" className="flex-1" disabled={categorySaving}>
                      {categorySaving
                        ? "Guardando..."
                        : editingCategoryId
                          ? "Actualizar categoria"
                          : "Crear categoria"}
                    </Button>
                    {editingCategoryId && (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={resetCategoryForm}
                        disabled={categorySaving}
                      >
                        Cancelar
                      </Button>
                    )}
                  </div>
                </form>

                <div className="border-t pt-4">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-sm font-semibold">Categorias registradas</p>
                    {categoriesLoading && (
                      <span className="text-xs text-gray-500">Cargando...</span>
                    )}
                  </div>
                  <div className="space-y-3">
                    {adminCategories.map((category) => (
                      <div
                        key={category.id}
                        className="rounded border border-gray-200 p-3 text-sm space-y-1"
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="space-y-1">
                            {category.image_url && (
                              <img
                                src={category.image_url}
                                alt={category.name}
                                className="h-16 w-24 rounded border object-cover"
                              />
                            )}
                            <p className="font-semibold">{category.name}</p>
                            <p className="text-xs text-gray-500">Slug: {category.slug}</p>
                            <p className="text-xs text-gray-500">
                              Plantilla:{" "}
                              {CATEGORY_TEMPLATE_OPTIONS.find(
                                (option) => option.value === category.template_slug,
                              )?.label || category.template_slug}
                            </p>
                            <p className="text-xs text-gray-500">
                              Estado: {category.is_active ? "Activa" : "Inactiva"}
                            </p>
                            {buildCategoryPropertySummary(category.properties).length > 0 && (
                              <p className="text-xs text-gray-500">
                                Propiedades: {buildCategoryPropertySummary(category.properties).join(" | ")}
                              </p>
                            )}
                            {category.description && (
                              <p className="text-xs text-gray-600 max-w-md">{category.description}</p>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => handleEditCategory(category)}
                            >
                              Editar
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => handleToggleCategoryStatus(category)}
                            >
                              {category.is_active ? "Desactivar" : "Activar"}
                            </Button>
                            {!category.is_system && (
                              <Button
                                type="button"
                                variant="destructive"
                                size="sm"
                                onClick={() => handleDeleteCategory(category)}
                              >
                                Eliminar
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                    {!categoriesLoading && adminCategories.length === 0 && (
                      <p className="text-xs text-gray-500">
                        No hay categorias registradas.
                      </p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className={`mt-6 ${activeModule === "terminos" ? "" : "hidden"}`}>
              <CardHeader>
                <CardTitle>Terminos y condiciones</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSaveTerms} className="space-y-4">
                  <Textarea
                    rows={14}
                    value={termsContent}
                    onChange={(event) => setTermsContent(event.target.value)}
                    placeholder="# 1. Envios y Entregas&#10;- Envio gratis: ...&#10;"
                    disabled={termsLoading}
                  />
                  <p className="text-xs text-gray-500">
                    Usa # para titulos, lineas con "-" para listas y deja una linea vacia entre bloques.
                  </p>
                  <Button type="submit" className="w-full" disabled={termsSaving || termsLoading}>
                    {termsSaving ? "Guardando..." : "Guardar terminos"}
                  </Button>
                </form>
              </CardContent>
            </Card>
            <Card className={`mt-6 ${activeModule === "privacidad" ? "" : "hidden"}`}>
              <CardHeader>
                <CardTitle>Politica de privacidad</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSavePrivacy} className="space-y-4">
                  <Textarea
                    rows={14}
                    value={privacyContent}
                    onChange={(event) => setPrivacyContent(event.target.value)}
                    placeholder="# Politica de Privacidad&#10;- ..."
                    disabled={privacyLoading}
                  />
                  <p className="text-xs text-gray-500">
                    Usa # para titulos, lineas con "-" para listas y deja una linea vacia entre bloques.
                  </p>
                  <Button
                    type="submit"
                    className="w-full"
                    disabled={privacySaving || privacyLoading}
                  >
                    {privacySaving ? "Guardando..." : "Guardar politica de privacidad"}
                  </Button>
                </form>
              </CardContent>
            </Card>
            <Card className={`mt-6 ${activeModule === "home" ? "" : "hidden"}`}>
              <CardHeader>
                <CardTitle>Configuracion de Home</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSaveHomeSettings} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="home-top-banner-text">Texto superior (franja negra)</Label>
                    <Textarea
                      id="home-top-banner-text"
                      rows={3}
                      value={topBannerText}
                      onChange={(event) => setTopBannerText(event.target.value)}
                      disabled={homeSettingsLoading}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="home-credit-promo-text">
                      Texto banner credito (debajo de la franja negra)
                    </Label>
                    <Textarea
                      id="home-credit-promo-text"
                      rows={2}
                      value={creditPromoText}
                      onChange={(event) => setCreditPromoText(event.target.value)}
                      disabled={homeSettingsLoading}
                    />
                  </div>

                  <div className="space-y-2">
                    <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 px-3 py-3 text-xs text-gray-600">
                      Aqui tambien puedes personalizar el texto y la imagen de cada banner de categoria.
                    </div>
                  </div>

                  <div className="space-y-2">
                    <p className="text-sm font-semibold">Banners de categorias</p>
                    <div className="space-y-4">
                      {CATEGORY_IMAGE_FIELDS.map((field) => (
                        <div className="space-y-3 rounded-lg border p-4" key={field.key}>
                          <Label htmlFor={`home-${field.key}`}>{field.label}</Label>
                          {homeImages[field.key] && (
                            <img
                              src={homeImages[field.key]}
                              alt={field.label}
                              className="h-24 w-full rounded border object-cover"
                            />
                          )}
                          <Input
                            key={`${field.key}-${homeImageInputVersion}`}
                            id={`home-${field.key}`}
                            type="file"
                            accept="image/*"
                            onChange={(event) =>
                              handleHomeImageFile(field.key, event.target.files?.[0] || null)
                            }
                          />
                          <div className="space-y-2">
                            <Label htmlFor={`home-${field.key}-category-title`}>Titulo</Label>
                            <Input
                              id={`home-${field.key}-category-title`}
                              value={categoryBannerContent?.[field.key]?.title || ""}
                              onChange={(event) =>
                                handleHomeCategoryBannerChange(
                                  field.key,
                                  "title",
                                  event.target.value,
                                )
                              }
                              disabled={homeSettingsLoading}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor={`home-${field.key}-category-subtitle`}>
                              Texto descriptivo
                            </Label>
                            <Textarea
                              id={`home-${field.key}-category-subtitle`}
                              rows={2}
                              value={categoryBannerContent?.[field.key]?.subtitle || ""}
                              onChange={(event) =>
                                handleHomeCategoryBannerChange(
                                  field.key,
                                  "subtitle",
                                  event.target.value,
                                )
                              }
                              disabled={homeSettingsLoading}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <p className="text-sm font-semibold">Banners principales</p>
                    <div className="space-y-4">
                      {HERO_IMAGE_FIELDS.map((field) => (
                        <div className="space-y-3 rounded-lg border p-4" key={field.key}>
                          <Label htmlFor={`home-${field.key}`}>{field.label}</Label>
                          {homeImages[field.key] && (
                            <img
                              src={homeImages[field.key]}
                              alt={field.label}
                              className="h-28 w-full rounded border object-cover"
                            />
                          )}
                          <Input
                            key={`${field.key}-${homeImageInputVersion}`}
                            id={`home-${field.key}`}
                            type="file"
                            accept="image/*"
                            onChange={(event) =>
                              handleHomeImageFile(field.key, event.target.files?.[0] || null)
                            }
                          />
                          <div className="space-y-2">
                            <Label htmlFor={`home-${field.key}-title`}>Titulo</Label>
                            <Input
                              id={`home-${field.key}-title`}
                              value={homeBannerContent?.[field.key]?.title || ""}
                              onChange={(event) =>
                                handleHomeBannerContentChange(
                                  field.key,
                                  "title",
                                  event.target.value,
                                )
                              }
                              disabled={homeSettingsLoading}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor={`home-${field.key}-subtitle`}>Subtitulo</Label>
                            <Textarea
                              id={`home-${field.key}-subtitle`}
                              rows={2}
                              value={homeBannerContent?.[field.key]?.subtitle || ""}
                              onChange={(event) =>
                                handleHomeBannerContentChange(
                                  field.key,
                                  "subtitle",
                                  event.target.value,
                                )
                              }
                              disabled={homeSettingsLoading}
                            />
                          </div>
                          <div className="grid gap-3 md:grid-cols-2">
                            <div className="space-y-2">
                              <Label htmlFor={`home-${field.key}-cta`}>Texto del boton</Label>
                              <Input
                                id={`home-${field.key}-cta`}
                                value={homeBannerContent?.[field.key]?.cta || ""}
                                onChange={(event) =>
                                  handleHomeBannerContentChange(
                                    field.key,
                                    "cta",
                                    event.target.value,
                                  )
                                }
                                disabled={homeSettingsLoading}
                              />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor={`home-${field.key}-link`}>Enlace del boton</Label>
                              <Input
                                id={`home-${field.key}-link`}
                                value={homeBannerContent?.[field.key]?.link || ""}
                                onChange={(event) =>
                                  handleHomeBannerContentChange(
                                    field.key,
                                    "link",
                                    event.target.value,
                                  )
                                }
                                disabled={homeSettingsLoading}
                              />
                            </div>
                          </div>
                          {field.key === "banner_4" && (
                            <div className="space-y-2">
                              <Label htmlFor={`home-${field.key}-steps`}>
                                Pasos del banner (una linea por paso)
                              </Label>
                              <Textarea
                                id={`home-${field.key}-steps`}
                                rows={6}
                                value={(homeBannerContent?.[field.key]?.steps || []).join("\n")}
                                onChange={(event) =>
                                  handleHomeBannerContentChange(
                                    field.key,
                                    "steps",
                                    event.target.value,
                                  )
                                }
                                disabled={homeSettingsLoading}
                              />
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <p className="text-sm font-semibold">
                      Banner de metodos de pago (hasta 10 logos)
                    </p>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      {PAYMENT_IMAGE_FIELDS.map((field) => (
                        <div className="space-y-2" key={field.key}>
                          <Label htmlFor={`home-${field.key}`}>{field.label}</Label>
                          {homeImages[field.key] && (
                            <img
                              src={homeImages[field.key]}
                              alt={field.label}
                              className="h-16 w-full rounded border object-contain bg-white p-1"
                            />
                          )}
                          <Input
                            key={`${field.key}-${homeImageInputVersion}`}
                            id={`home-${field.key}`}
                            type="file"
                            accept="image/*"
                            onChange={(event) =>
                              handleHomeImageFile(field.key, event.target.files?.[0] || null)
                            }
                          />
                        </div>
                      ))}
                    </div>
                  </div>

                  <Button
                    type="submit"
                    className="w-full"
                    disabled={homeSettingsSaving || homeSettingsLoading}
                  >
                    {homeSettingsSaving ? "Guardando..." : "Guardar Home"}
                  </Button>
                </form>
              </CardContent>
            </Card>
            <Card className={`mt-6 ${activeModule === "vendedores" ? "" : "hidden"}`}>
              <CardHeader>
                <CardTitle>Vendedores</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <form onSubmit={handleVendorSubmit} className="space-y-3">
                  <div className="space-y-2">
                    <Label htmlFor="vendor-name">Nombre</Label>
                    <Input
                      id="vendor-name"
                      value={vendorForm.name}
                      onChange={(event) =>
                        setVendorForm((prev) => ({ ...prev, name: event.target.value }))
                      }
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="vendor-id-number">Cedula</Label>
                    <Input
                      id="vendor-id-number"
                      value={vendorForm.idNumber}
                      onChange={(event) =>
                        setVendorForm((prev) => ({ ...prev, idNumber: event.target.value }))
                      }
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="vendor-email">Correo</Label>
                    <Input
                      id="vendor-email"
                      type="email"
                      value={vendorForm.email}
                      onChange={(event) =>
                        setVendorForm((prev) => ({ ...prev, email: event.target.value }))
                      }
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="vendor-address">Direccion</Label>
                    <Input
                      id="vendor-address"
                      value={vendorForm.address}
                      onChange={(event) =>
                        setVendorForm((prev) => ({ ...prev, address: event.target.value }))
                      }
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="vendor-phone">Telefono WhatsApp</Label>
                    <Input
                      id="vendor-phone"
                      value={vendorForm.whatsappPhone}
                      onChange={(event) =>
                        setVendorForm((prev) => ({ ...prev, whatsappPhone: event.target.value }))
                      }
                      required
                    />
                  </div>
                  <Button type="submit" className="w-full" disabled={vendorSaving}>
                    {vendorSaving ? "Guardando..." : "Guardar vendedor"}
                  </Button>
                </form>

                <form onSubmit={handleVendorTemplateSubmit} className="border-t pt-4 space-y-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <p className="text-sm font-semibold">
                      Plantillas de mensajes para vendedores
                    </p>
                    <div className="flex items-center gap-2">
                      {vendorTemplatesLoading && (
                        <span className="text-xs text-gray-500">Cargando...</span>
                      )}
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={handleResetVendorTemplateForm}
                      >
                        Nueva plantilla
                      </Button>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Mensajes guardados</Label>
                    <Select
                      value={editingVendorTemplateId ? String(editingVendorTemplateId) : ""}
                      onValueChange={(value) => {
                        const selectedTemplate =
                          vendorTemplates.find(
                            (template) => String(template.id) === String(value),
                          ) || null;
                        if (selectedTemplate) {
                          handleEditVendorTemplate(selectedTemplate);
                        }
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecciona una plantilla para editarla" />
                      </SelectTrigger>
                      <SelectContent>
                        {vendorTemplates.map((template) => (
                          <SelectItem key={template.id} value={String(template.id)}>
                            {template.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="vendor-template-name">Nombre de la plantilla</Label>
                    <Input
                      id="vendor-template-name"
                      value={vendorTemplateForm.name}
                      onChange={(event) =>
                        setVendorTemplateForm((prev) => ({
                          ...prev,
                          name: event.target.value,
                        }))
                      }
                      placeholder="Ej: Bienvenida base vendedores"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="vendor-template-message">Mensaje guardado</Label>
                    <Textarea
                      id="vendor-template-message"
                      rows={5}
                      value={vendorTemplateForm.message}
                      onChange={(event) =>
                        setVendorTemplateForm((prev) => ({
                          ...prev,
                          message: event.target.value,
                        }))
                      }
                      placeholder="Hola {nombre}, bienvenido a Eclipse Tenis."
                    />
                    <p className="text-xs text-gray-500">
                      Usa {"{nombre}"} para insertar automaticamente el nombre del vendedor.
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button type="submit" disabled={vendorTemplateSaving}>
                      {vendorTemplateSaving
                        ? "Guardando..."
                        : editingVendorTemplateId
                          ? "Actualizar plantilla"
                          : "Guardar plantilla"}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleUseCurrentVendorTemplateInWelcomeForm}
                    >
                      Usar en el envio
                    </Button>
                    {editingVendorTemplateId && (
                      <Button
                        type="button"
                        variant="outline"
                        disabled={vendorTemplateDeletingId === editingVendorTemplateId}
                        onClick={() =>
                          handleDeleteVendorTemplate({
                            id: editingVendorTemplateId,
                            name: vendorTemplateForm.name,
                          })
                        }
                      >
                        {vendorTemplateDeletingId === editingVendorTemplateId
                          ? "Eliminando..."
                          : "Eliminar plantilla"}
                      </Button>
                    )}
                  </div>
                </form>

                <form
                  onSubmit={handleSendVendorWelcomeEmail}
                  className="border-t pt-4 space-y-3"
                >
                  <p className="text-sm font-semibold">
                    Enviar mensaje de bienvenida por correo
                  </p>
                  <div className="space-y-2">
                    <Label>Vendedor</Label>
                    <Select
                      value={vendorWelcomeForm.vendorId}
                      onValueChange={(value) =>
                        setVendorWelcomeForm((prev) => ({ ...prev, vendorId: value }))
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecciona vendedor" />
                      </SelectTrigger>
                      <SelectContent>
                        {vendors.map((vendor) => (
                          <SelectItem key={vendor.id} value={String(vendor.id)}>
                            {vendor.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Plantilla a usar</Label>
                    <Select
                      value={vendorWelcomeForm.templateId}
                      onValueChange={handleVendorTemplateSelectionChange}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecciona una plantilla guardada" />
                      </SelectTrigger>
                      <SelectContent>
                        {vendorTemplates.map((template) => (
                          <SelectItem key={template.id} value={String(template.id)}>
                            {template.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-gray-500">
                      Puedes elegir una plantilla guardada y luego ajustar el texto antes de enviarlo por correo.
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="vendor-welcome-message">Mensaje final a enviar</Label>
                    <Textarea
                      id="vendor-welcome-message"
                      rows={4}
                      value={vendorWelcomeForm.message}
                      onChange={(event) =>
                        setVendorWelcomeForm((prev) => ({
                          ...prev,
                          message: event.target.value,
                        }))
                      }
                      placeholder="Hola {nombre}, bienvenido a Eclipse Tenis."
                    />
                    <p className="text-xs text-gray-500">
                      {selectedVendorTemplate
                        ? `Plantilla seleccionada: ${selectedVendorTemplate.name}`
                        : "Si no eliges plantilla, puedes escribir el mensaje manualmente."}
                    </p>
                  </div>
                    <Button
                    type="submit"
                    className="w-full"
                    disabled={
                      vendorWelcomeSending || vendorsLoading || vendors.length === 0
                    }
                  >
                    {vendorWelcomeSending
                      ? "Enviando..."
                      : "Enviar bienvenida por correo"}
                  </Button>
                </form>

                <div className="border-t pt-4">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-sm font-semibold">Vendedores registrados</p>
                    {vendorsLoading && (
                      <span className="text-xs text-gray-500">Cargando...</span>
                    )}
                  </div>
                  <div className="space-y-3">
                    {vendors.map((vendor) => (
                      <div
                        key={vendor.id}
                        className="rounded border border-gray-200 p-3 text-sm space-y-1"
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <p className="font-semibold">{vendor.name}</p>
                            <p className="text-xs text-gray-500">Cedula: {vendor.id_number}</p>
                          </div>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => handleVendorDelete(vendor.id)}
                          >
                            Eliminar
                          </Button>
                        </div>
                        <p className="text-xs text-gray-500">Correo: {vendor.email}</p>
                        <p className="text-xs text-gray-500">Direccion: {vendor.address}</p>
                        <p className="text-xs text-gray-500">
                          WhatsApp: {vendor.whatsapp_phone}
                        </p>
                        <p className="text-xs text-gray-500">
                          Ventas: {vendor.sales_count ?? 0}
                        </p>
                        <p className="text-xs text-gray-500">
                          Total ventas: $
                          {Number(vendor.sales_total || 0).toLocaleString("es-CO")}
                        </p>
                      </div>
                    ))}
                    {!vendorsLoading && vendors.length === 0 && (
                      <p className="text-xs text-gray-500">
                        No hay vendedores registrados.
                      </p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className={`mt-6 ${activeModule === "newsletter" ? "" : "hidden"}`}>
              <CardHeader>
                <CardTitle>Suscripciones</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm font-semibold">Clientes registrados</p>
                  {newsletterLoading && (
                    <span className="text-xs text-gray-500">Cargando...</span>
                  )}
                </div>
                <div className="space-y-3">
                  {newsletterSubscribers.map((subscriber) => (
                    <div
                      key={subscriber.id}
                      className="rounded border border-gray-200 p-3 text-sm space-y-1"
                    >
                      <p className="font-semibold break-all">{subscriber.email}</p>
                      <p className="text-xs text-gray-500">
                        WhatsApp: {subscriber.whatsapp_phone}
                      </p>
                      <p className="text-xs text-gray-500">
                        Registro:{" "}
                        {subscriber.created_at
                          ? new Date(subscriber.created_at).toLocaleString("es-CO")
                          : "-"}
                      </p>
                    </div>
                  ))}
                  {!newsletterLoading && newsletterSubscribers.length === 0 && (
                    <p className="text-xs text-gray-500">
                      No hay suscriptores registrados.
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
            <Card className={`mt-6 ${activeModule === "cupones" ? "" : "hidden"}`}>
              <CardHeader>
                <CardTitle>Cupones</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <form onSubmit={handleCouponSubmit} className="space-y-3">
                  <div className="space-y-2">
                    <Label>Tipo de descuento</Label>
                    <Select
                      value={couponForm.discountType}
                      onValueChange={(value) =>
                        setCouponForm((prev) => ({ ...prev, discountType: value }))
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecciona tipo" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="percent">Por porcentaje</SelectItem>
                        <SelectItem value="amount">Valor fijo (COP)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {couponForm.discountType === "percent" ? (
                    <div className="space-y-2">
                      <Label htmlFor="coupon-percent">Porcentaje de descuento</Label>
                      <Input
                        id="coupon-percent"
                        type="number"
                        min="0.1"
                        step="0.1"
                        value={couponForm.percent}
                        onChange={(event) =>
                          setCouponForm((prev) => ({ ...prev, percent: event.target.value }))
                        }
                        required
                      />
                      <p className="text-xs text-gray-500">
                        Ejemplo: 10 para 10%.
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <Label htmlFor="coupon-amount">Valor fijo del cupon (COP)</Label>
                      <Input
                        id="coupon-amount"
                        type="number"
                        min="1"
                        step="1"
                        value={couponForm.amountOff}
                        onChange={(event) =>
                          setCouponForm((prev) => ({
                            ...prev,
                            amountOff: event.target.value,
                          }))
                        }
                        required
                      />
                      <p className="text-xs text-gray-500">
                        Ejemplo: 20000 para descontar $20.000.
                      </p>
                    </div>
                  )}
                  <div className="space-y-2">
                    <Label htmlFor="coupon-min-amount">Compra minima (COP)</Label>
                    <Input
                      id="coupon-min-amount"
                      type="number"
                      min="0"
                      step="1"
                      value={couponForm.minAmount}
                      onChange={(event) =>
                        setCouponForm((prev) => ({ ...prev, minAmount: event.target.value }))
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="coupon-prefix">Prefijo del codigo (opcional)</Label>
                    <Input
                      id="coupon-prefix"
                      value={couponForm.codePrefix}
                      onChange={(event) =>
                        setCouponForm((prev) => ({ ...prev, codePrefix: event.target.value }))
                      }
                      placeholder="ET"
                    />
                  </div>
                  <Button type="submit" className="w-full" disabled={couponSaving}>
                    {couponSaving ? "Generando..." : "Generar cupon"}
                  </Button>
                  <p className="text-xs text-gray-500">
                    Los cupones creados en este formulario vencen automaticamente en 3 meses.
                  </p>
                </form>

                <div className="border-t pt-4">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-sm font-semibold">Cupones registrados</p>
                    {couponsLoading && (
                      <span className="text-xs text-gray-500">Cargando...</span>
                    )}
                  </div>
                  <div className="space-y-3">
                    {coupons.map((coupon) => {
                      const isUsed = Boolean(coupon.used);
                      const isActive = Boolean(coupon.is_active);
                      const source = String(coupon.source || "").toLowerCase();
                      const canDisable = source === "admin" && isActive && !isUsed;
                      const sourceLabel = coupon.source_label || coupon.source || "Sin origen";
                      const stateLabel =
                        coupon.state_label ||
                        (isUsed ? "Usado" : isActive ? "Activo" : "Inactivo");
                      const expiresAt = coupon.expires_at
                        ? formatDateTime(coupon.expires_at)
                        : "Sin vencimiento";
                      const timeLeft = formatCouponTimeLeft(coupon.expires_in_seconds);
                      const createdAt = formatDateTime(coupon.created_at);
                      const discountLabel = formatCouponDiscount(coupon);
                      return (
                        <div
                          key={coupon.id}
                          className="rounded border border-gray-200 p-3 text-sm space-y-1"
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div>
                              <p className="font-semibold">{coupon.code}</p>
                              <p className="text-xs text-gray-500">
                                Origen: {sourceLabel}
                              </p>
                              <p className="text-xs text-gray-500">
                                Descuento: {discountLabel}
                              </p>
                              <p className="text-xs text-gray-500">
                                Minimo: {formatCop(coupon.min_amount)}
                              </p>
                              <p className="text-xs text-gray-500">
                                Creado: {createdAt}
                              </p>
                              <p className="text-xs text-gray-500">
                                Vence: {expiresAt}
                              </p>
                              <p className="text-xs text-gray-500">
                                Tiempo restante: {timeLeft}
                              </p>
                              <p className="text-xs text-gray-500">
                                Estado: {stateLabel}
                              </p>
                              {coupon.customer_email && (
                                <p className="text-xs text-gray-500 break-all">
                                  Cliente: {coupon.customer_email}
                                </p>
                              )}
                              {coupon.issued_order_code && (
                                <p className="text-xs text-gray-500">
                                  Generado por pedido: {coupon.issued_order_code}
                                </p>
                              )}
                              {coupon.used_order_code && (
                                <p className="text-xs text-gray-500">
                                  Usado en pedido: {coupon.used_order_code}
                                </p>
                              )}
                            </div>
                            {canDisable && (
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => handleDisableCoupon(coupon.id)}
                              >
                                Desactivar
                              </Button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                    {!couponsLoading && coupons.length === 0 && (
                      <p className="text-xs text-gray-500">
                        Aun no hay cupones registrados.
                      </p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className={`mt-6 ${activeModule === "mensaje_final" ? "" : "hidden"}`}>
              <CardHeader>
                <CardTitle>Mensaje final al comprador</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSavePurchaseMessage} className="space-y-4">
                  <Textarea
                    rows={14}
                    value={purchaseMessageContent}
                    onChange={(event) => setPurchaseMessageContent(event.target.value)}
                    disabled={purchaseMessageLoading}
                    placeholder="Hola {customer_name}, recibimos tu pago..."
                  />
                  <div className="rounded border border-gray-200 bg-gray-50 p-3 text-xs text-gray-600">
                    <p className="font-semibold mb-1">Variables disponibles:</p>
                    <p>
                      {"{customer_name}"}, {"{order_code}"}, {"{buyer_name}"}, {"{buyer_phone}"}, {"{buyer_id_number}"}
                    </p>
                    <p>
                      {"{items_text}"}, {"{subtotal}"}, {"{discount}"}, {"{shipping_cost}"}, {"{total}"}
                    </p>
                    <p>
                      {"{address_text}"}, {"{coupon_code}"}
                    </p>
                  </div>
                  <Button
                    type="submit"
                    className="w-full"
                    disabled={purchaseMessageSaving || purchaseMessageLoading}
                  >
                    {purchaseMessageSaving ? "Guardando..." : "Guardar mensaje final"}
                  </Button>
                </form>
              </CardContent>
            </Card>
            <Card className={`mt-6 ${activeModule === "resenas" ? "" : "hidden"}`}>
              <CardHeader>
                <CardTitle>Reseñas publicadas</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm font-semibold">Opiniones de clientes</p>
                  {reviewsLoading && (
                    <span className="text-xs text-gray-500">Cargando...</span>
                  )}
                </div>
                <div className="space-y-3">
                  {reviews.map((review) => (
                    <div
                      key={review.id}
                      className="rounded border border-gray-200 p-3 text-sm space-y-1"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <p className="font-semibold">{review.name || "Cliente"}</p>
                          <p className="text-xs text-gray-500">
                            Calificacion: {review.rating}/5
                          </p>
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => handleDeleteReview(review.id)}
                        >
                          Eliminar
                        </Button>
                      </div>
                      {review.email && (
                        <p className="text-xs text-gray-500 break-all">{review.email}</p>
                      )}
                      <p className="text-sm text-gray-700">{review.comment}</p>
                      <p className="text-xs text-gray-500">
                        {review.created_at
                          ? new Date(review.created_at).toLocaleString("es-CO")
                          : "-"}
                      </p>
                    </div>
                  ))}
                  {!reviewsLoading && reviews.length === 0 && (
                    <p className="text-xs text-gray-500">
                      Aun no hay reseñas registradas.
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          <div
            className={`lg:col-span-2 space-y-4 ${
              activeModule === "pedidos" || activeModule === "productos" ? "" : "hidden"
            }`}
          >
            <Card className={activeModule === "pedidos" ? "" : "hidden"}>
              <CardHeader>
                <CardTitle>Pedidos registrados</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm font-semibold">Historial de compras</p>
                  {ordersLoading && (
                    <span className="text-xs text-gray-500">Cargando...</span>
                  )}
                </div>
                <div className="space-y-4 max-h-[420px] overflow-y-auto pr-1">
                  {orders.map((order) => (
                    <div
                      key={order.id}
                      className="rounded border border-gray-200 p-3 text-sm space-y-2"
                    >
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <p className="font-semibold">{order.order_code}</p>
                        <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs capitalize">
                          {order.payment_status}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500">
                        Cliente: {order.customer_name} {order.customer_last_name} | Cedula:{" "}
                        {order.customer_id_number}
                      </p>
                      <p className="text-xs text-gray-500">
                        Correo: {order.email} | Telefono: {order.phone}
                      </p>
                      <p className="text-xs text-gray-500">
                        Direccion: {order.address}, {order.city}, {order.state}
                      </p>
                      <p className="text-xs text-gray-500">
                        Metodo: {order.payment_method} | Vendedor: {order.vendor_name || "-"}
                      </p>
                      <p className="text-xs text-gray-500">
                        Subtotal: {formatCop(order.subtotal)} | Descuento: {formatCop(order.discount)} | Envio:{" "}
                        {formatCop(order.shipping_cost)} | Total: {formatCop(order.total)}
                      </p>
                      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                        {Array.isArray(order.items) &&
                          order.items.map((item) => (
                            <div
                              key={`${order.id}-${item.id}`}
                              className="rounded border border-gray-200 p-2 text-xs"
                            >
                              <div className="flex gap-2">
                                {item.image_url && (
                                  <img
                                    src={item.image_url}
                                    alt={item.name}
                                    className="h-12 w-12 rounded object-cover"
                                  />
                                )}
                                <div className="space-y-0.5">
                                  <p className="font-semibold">{item.name}</p>
                                  <p>
                                    Cantidad: {item.quantity} | Total: {formatCop(item.total)}
                                  </p>
                                  {item.size && <p>Talla: {item.size}</p>}
                                  {item.color && <p>Color: {item.color}</p>}
                                </div>
                              </div>
                            </div>
                          ))}
                      </div>
                    </div>
                  ))}
                  {!ordersLoading && orders.length === 0 && (
                    <p className="text-xs text-gray-500">
                      No hay pedidos registrados.
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
            <div
              className={`flex items-center justify-between ${
                activeModule === "productos" ? "" : "hidden"
              }`}
            >
              <h2 className="text-xl font-bold">Productos</h2>
              {loading && <span className="text-sm text-gray-500">Cargando...</span>}
            </div>
            <div
              className={`grid grid-cols-1 md:grid-cols-2 gap-4 ${
                activeModule === "productos" ? "" : "hidden"
              }`}
            >
              {productos.map((product) => {
                const discountPercent = calculateDiscountPercent(product.price, product.sale_price);
                return (
                <Card key={product.id}>
                  <CardContent className="p-4 space-y-3">
                    <div className="flex gap-4">
                      <div className="w-20 h-20 rounded overflow-hidden bg-gray-100">
                        <img
                          src={product.image_url}
                          alt={product.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold">{product.name}</h3>
                        {product.brand && (
                          <p className="text-sm text-gray-500">{product.brand}</p>
                        )}
                        {product.color && (
                          <p className="text-xs text-gray-500">Color: {product.color}</p>
                        )}
                        <p className="text-xs text-gray-500">
                          Stock: {product.stock ?? 0}
                        </p>
                        {product.category !== "unisex" && (
                          <p className="text-sm text-gray-600 capitalize">{product.category}</p>
                        )}
                        {product.whatsapp_only && (
                          <p className="text-xs font-semibold text-emerald-700">
                            Solo WhatsApp
                          </p>
                        )}
                        <p className="text-sm font-bold">
                          ${Number(product.sale_price || product.price).toLocaleString("es-CO")}
                        </p>
                        {product.sale_price && (
                          <>
                            <p className="text-xs text-gray-500 line-through">
                              ${Number(product.price).toLocaleString("es-CO")}
                            </p>
                            {discountPercent > 0 && (
                              <p className="text-xs font-semibold text-red-600">
                                Descuento: {discountPercent}%
                              </p>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        className="flex-1"
                        onClick={() => handleEdit(product)}
                      >
                        Editar
                      </Button>
                      <Button
                        variant="destructive"
                        className="flex-1"
                        onClick={() => handleDelete(product.id)}
                      >
                        Eliminar
                      </Button>
                    </div>
                  </CardContent>
                </Card>
                );
              })}
              {!loading && productos.length === 0 && (
                <div className="text-gray-500 text-sm">No hay productos registrados.</div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Admin;




